import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_ANON_KEY);

const LEVELS       = ['Beginner', 'Intermediate'];
const LESSON_TYPES = ['Individual lesson', 'Elite Squad Individual', 'Elite Squad', 'Kickstart', 'Speaking Club', 'Plantão de dúvidas'];
const TEACHERS     = ['Victor', 'Beatriz', 'Pedagogical Team'];

// Common timezones covering where teachers and students are actually based.
const TIMEZONES = [
  { value: 'America/Sao_Paulo',       label: 'Brasília (Brazil)' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires (Argentina)' },
  { value: 'America/Bogota',          label: 'Bogotá (Colombia)' },
  { value: 'America/Mexico_City',     label: 'Mexico City' },
  { value: 'America/New_York',        label: 'New York (US Eastern)' },
  { value: 'America/Chicago',         label: 'Chicago (US Central)' },
  { value: 'America/Los_Angeles',     label: 'Los Angeles (US Pacific)' },
  { value: 'Europe/Madrid',           label: 'Madrid / Barcelona (Spain)' },
  { value: 'Europe/London',           label: 'London (UK)' },
  { value: 'Europe/Lisbon',           label: 'Lisbon (Portugal)' },
  { value: 'UTC',                     label: 'UTC' },
];

// Converts a wall-clock date/time as understood in a given IANA timezone into a true UTC Date.
// Handles DST correctly because it re-checks the offset at that specific date, not a fixed number.
function zonedTimeToUtc(dateStr, timeStr, timeZone) {
  const naive = new Date(`${dateStr}T${timeStr}:00Z`); // treat input as if it were UTC, as a starting guess
  const asIfLocal = new Date(naive.toLocaleString('en-US', { timeZone }));
  const asIfUtc = new Date(naive.toLocaleString('en-US', { timeZone: 'UTC' }));
  const offsetMs = asIfUtc.getTime() - asIfLocal.getTime();
  return new Date(naive.getTime() + offsetMs);
}
const ATT = {
  present: { label: 'Presente', color: '#16a34a', bg: '#dcfce7' },
  late:    { label: 'Atrasado', color: '#d97706', bg: '#fef3c7' },
  absent:  { label: 'Faltou',   color: '#dc2626', bg: '#fee2e2' },
};
// NOTE: speaking_club and individual_vip are kept here (labels/colors/duration)
// so existing students on those legacy plans keep displaying and filtering
// correctly. As of this update, new students may only be assigned Kickstart
// or Elite Squad — see SELLABLE_PLANS below.
const PLAN_LABELS = { speaking_club: 'Speaking Club', individual_vip: 'Individual VIP', elite_squad: 'Elite Squad', kickstart: 'Kickstart' };
const PLAN_COLORS = {
  speaking_club:  { bg: '#dcfce7', color: '#166534' },
  individual_vip: { bg: '#dbeafe', color: '#1d4ed8' },
  elite_squad:    { bg: '#fef3c7', color: '#d97706' },
  kickstart:      { bg: '#dcfce7', color: '#16a349' },
};
const PLAN_MONTHS = { elite_squad: 12, individual_vip: 3, speaking_club: 1, kickstart: 4 };
// The only plans staff can assign to a student (new signups or plan changes) going forward.
const SELLABLE_PLANS = ['kickstart', 'elite_squad'];

const COMM_CATEGORY_ICON = { restaurant: '🍽️', cafe: '☕', place_to_visit: '📍', other: '✨' };
const COMM_CATEGORY_LABEL = { restaurant: 'Restaurante', cafe: 'Café', place_to_visit: 'Lugar para visitar', other: 'Outro' };
const COMM_MEDIA_ICON = { movie: '🎬', podcast: '🎧', book: '📚' };
const COMM_MEDIA_LABEL = { movie: 'Filme/Série', podcast: 'Podcast', book: 'Livro' };

const calcContractEnd = (plan, start) => {
  if (!start || !plan) return '';
  const d = new Date(start);
  d.setMonth(d.getMonth() + (PLAN_MONTHS[plan] || 1));
  return d.toISOString().split('T')[0];
};
const daysLeft = (end) => {
  if (!end) return null;
  return Math.ceil((new Date(end) - new Date()) / 86400000);
};
const expiryStyle = (days) => {
  if (days === null) return { color: '#94a3b8', bg: '#f8fafc' };
  if (days < 0)   return { color: '#dc2626', bg: '#fee2e2' };
  if (days <= 30) return { color: '#dc2626', bg: '#fee2e2' };
  if (days <= 60) return { color: '#d97706', bg: '#fef3c7' };
  return { color: '#16a34a', bg: '#dcfce7' };
};

const adminStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Raleway:wght@400;600;700;800;900&display=swap');
  * { box-sizing: border-box; }
  body { margin: 0; font-family: 'Raleway', sans-serif; background: #f0f4f8; color: #0f172a; }
  .admin-table { width: 100%; border-collapse: collapse; }
  .admin-table thead { background: linear-gradient(135deg, #1a2a6c, #2563eb); color: white; }
  .admin-table th { padding: 14px 18px; text-align: left; font-weight: 700; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 1px; white-space: nowrap; }
  .admin-table td { padding: 14px 18px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
  .admin-table tbody tr:hover { background: #f8fafc; }
  .admin-table tbody tr:last-child td { border-bottom: none; }
  .table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
  .card { background: white; border-radius: 16px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); overflow: hidden; }
  .badge { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; border-radius: 50px; font-size: 0.72rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; white-space: nowrap; }
  .btn-action { padding: 7px 14px; border-radius: 8px; font-weight: 700; font-size: 0.8rem; cursor: pointer; border: 1px solid; transition: opacity 0.15s; font-family: 'Raleway', sans-serif; }
  .btn-action:hover { opacity: 0.8; }
  .modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.75); backdrop-filter: blur(6px); display: flex; align-items: center; justify-content: center; z-index: 1000; }
  .modal-box { background: white; border-radius: 20px; width: 90%; box-shadow: 0 30px 60px rgba(0,0,0,0.25); max-height: 90vh; overflow-y: auto; position: relative; }
  .modal-close { position: absolute; top: 16px; right: 16px; background: #f1f5f9; border: none; border-radius: 50%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 1.1rem; color: #64748b; line-height: 1; z-index: 10; transition: background 0.15s; }
  .modal-close:hover { background: #e2e8f0; color: #0f172a; }
  .att-btn { flex: 1; padding: 8px 6px; border-radius: 8px; font-size: 0.78rem; font-weight: 800; cursor: pointer; border: 2px solid; transition: all 0.15s; text-align: center; font-family: 'Raleway', sans-serif; }
  .profile-section { margin-bottom: 24px; }
  .profile-section-title { font-size: 0.75rem; font-weight: 800; color: #94a3b8; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 12px; }
  .stat-card { background: white; border-radius: 14px; padding: 20px 24px; flex: 1; min-width: 150px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); position: relative; }
  .filter-input { padding: 10px 14px; border-radius: 10px; border: 1.5px solid #e2e8f0; outline: none; font-family: 'Raleway', sans-serif; font-weight: 600; color: #0f172a; font-size: 0.875rem; background: white; }
  .filter-input:focus { border-color: #1a2a6c; }
  textarea { font-family: 'Raleway', sans-serif; font-size: 0.875rem; resize: vertical; }
  .tooltip-box { position: absolute; top: calc(100% + 10px); left: 50%; transform: translateX(-50%); background: #0f172a; color: white; padding: 10px 14px; border-radius: 10px; font-size: 0.78rem; font-weight: 600; line-height: 1.5; width: 240px; text-align: center; z-index: 200; box-shadow: 0 8px 24px rgba(0,0,0,0.2); pointer-events: none; font-family: 'Raleway', sans-serif; }
  .tooltip-arrow-up { position: absolute; bottom: 100%; left: 50%; transform: translateX(-50%); border-width: 6px; border-style: solid; border-color: transparent transparent #0f172a transparent; }
  .info-btn { background: #f1f5f9; border: none; border-radius: 50%; width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 0.65rem; font-weight: 800; color: #94a3b8; flex-shrink: 0; padding: 0; line-height: 1; }
  .info-btn:hover { background: #e2e8f0; }
  .fab { position: fixed; bottom: 32px; right: 32px; background: linear-gradient(135deg, #16a34a, #15803d); color: white; border: none; border-radius: 50px; padding: 20px 36px; font-size: 1.15rem; font-weight: 900; cursor: pointer; font-family: 'Raleway', sans-serif; box-shadow: 0 10px 30px rgba(22,163,74,0.45); display: flex; align-items: center; gap: 12px; z-index: 50; transition: transform 0.15s, box-shadow 0.15s; letter-spacing: 0.5px; }
  .fab:hover { transform: translateY(-3px); box-shadow: 0 14px 36px rgba(22,163,74,0.5); }
  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: #f1f5f9; }
  ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
`;

export default function Admin() {
  const [user, setUser] = useState(null);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [lessons, setLessons] = useState([]);
  const [students, setStudents] = useState([]);
  const [allAttendance, setAllAttendance] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('lessons');
  const [tooltipVisible, setTooltipVisible] = useState(null);
  const [communityPendingCount, setCommunityPendingCount] = useState(0);

  const refreshCommunityPendingCount = useCallback(async () => {
    const [{ count: c1 }, { count: c2 }] = await Promise.all([
      supabase.from('community_posts').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
      supabase.from('community_media').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
    ]);
    setCommunityPendingCount((c1 || 0) + (c2 || 0));
  }, []);

  const [teacherFilter, setTeacherFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [studentSearch, setStudentSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [showPast, setShowPast] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedLessonId, setSelectedLessonId] = useState(null);
  const [selectedLessonOriginal, setSelectedLessonOriginal] = useState(null);
  const [editBulk, setEditBulk] = useState(false);
  const [formData, setFormData] = useState({
    title: 'Individual lesson', teacher_name: 'Victor', level: 'Beginner',
    date: '', time: '09:00', recurrence: 'none', recurrenceEnd: '', meet_link: ''
  });

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [lessonToDelete, setLessonToDelete] = useState(null);
  const [deleteScope, setDeleteScope] = useState('single');

  const [attModalOpen, setAttModalOpen] = useState(false);
  const [attLesson, setAttLesson] = useState(null);
  const [attData, setAttData] = useState({});
  const [matData, setMatData] = useState({ content_covered: '', homework: '', teacher_notes: '' });

  const [profileOpen, setProfileOpen] = useState(false);
  const [selStudent, setSelStudent] = useState(null);
  const [profileNote, setProfileNote] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);

  const [studentModalOpen, setStudentModalOpen] = useState(false);
  const [studentForm, setStudentForm] = useState({
    full_name: '', email: '', level: 'Beginner', plan: 'kickstart',
    contract_start: '', phone: '', is_trial: false, language: 'english', ui_language: 'pt',
  });

  const [trialPopupOpen, setTrialPopupOpen] = useState(false);
  const [trialStudent, setTrialStudent] = useState(null);
  const [trialNote, setTrialNote] = useState('');
  const [trialSaving, setTrialSaving] = useState(false);

  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ email: '', phone: '', full_name: '' });
  const [contactSaving, setContactSaving] = useState(false);

  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  const fetchAll = useCallback(async () => {
    const [
      { data: l }, { data: s }, { data: w }, { data: st },
      { data: att }, { data: lp }, { data: mat }, { data: pn },
      { data: refl }, { data: chk }, { data: canc },
    ] = await Promise.all([
      supabase.from('lessons').select('*').order('class_time', { ascending: true }),
      supabase.from('signups').select('*'),
      supabase.from('waitlist').select('*'),
      supabase.from('students').select('*').order('full_name', { ascending: true }),
      supabase.from('attendance').select('*'),
      supabase.from('level_progression').select('*').order('changed_at', { ascending: false }),
      supabase.from('lesson_materials').select('*'),
      supabase.from('student_profile_notes').select('*'),
      supabase.from('reflections').select('*').order('created_at', { ascending: false }),
      supabase.from('checkins').select('*').order('created_at', { ascending: false }),
      supabase.from('cancellations').select('*'),
    ]);

    const twoWeeksAgo = new Date(now - 14 * 24 * 60 * 60 * 1000);

    const lessonList = (l || []).map(lesson => ({
      ...lesson,
      enrolled: (s || []).filter(sig => sig.lesson_id === lesson.id),
      waiting:  (w || []).filter(wait => wait.lesson_id === lesson.id),
      materials: (mat || []).find(m => m.lesson_id === lesson.id) || null,
      attendanceMarked: (att || []).some(a => a.lesson_id === lesson.id),
    }));

    const studentList = (st || []).map(student => {
      const signups = (s || []).filter(sig => sig.student_email === student.email);
      const lessonTimes = signups.map(sig => {
        const lesson = (l || []).find(le => le.id === sig.lesson_id);
        return lesson ? new Date(lesson.class_time) : null;
      }).filter(Boolean);
      const lastBooking = lessonTimes.length > 0 ? new Date(Math.max(...lessonTimes)) : null;
      const hasFuture   = lessonTimes.some(t => t > now);
      const isAtRisk    = student.contract_status !== 'cancelled' && !hasFuture && (!lastBooking || lastBooking < twoWeeksAgo);
      const myAtt       = (att || []).filter(a => a.student_email === student.email);
      const attRate     = myAtt.length > 0 ? Math.round((myAtt.filter(a => a.status === 'present').length / myAtt.length) * 100) : null;
      const scAtt       = myAtt.filter(a => {
        const les = (l || []).find(le => le.id === a.lesson_id);
        return les?.title === 'Speaking Club' && a.status === 'present';
      }).length;
      return {
        ...student,
        profileNote: (pn || []).find(n => n.student_id === student.id)?.note || '',
        lastBooking, isAtRisk, hasFuture, attRate, scAtt,
        totalAttended: myAtt.filter(a => a.status === 'present').length,
        totalMarked: myAtt.length,
        levelHistory: (lp || []).filter(h => h.student_id === student.id),
        attRecords: myAtt,
        reflections: (refl || []).filter(r => r.student_id === student.id),
        checkins: (chk || []).filter(c => c.student_id === student.id),
        cancellations: (canc || []).filter(c => c.student_email === student.email),
      };
    });

    setLessons(lessonList);
    setStudents(studentList);
    setAllAttendance(att || []);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) return;
      supabase.from('students').select('role').eq('email', session.user.email).single().then(({ data }) => {
        if (data?.role === 'admin') { setUser(session.user); fetchAll(); refreshCommunityPendingCount(); }
        else supabase.auth.signOut();
      });
    });
  }, [fetchAll, refreshCommunityPendingCount]);

  const handleLogin = async (e) => {
    e.preventDefault(); setLoading(true);
    const { data, error } = await supabase.auth.signInWithPassword({ email: loginEmail.trim(), password: loginPassword });
    if (error) { alert('Login falhou: ' + error.message); setLoading(false); return; }
    const { data: r } = await supabase.from('students').select('role').eq('id', data.user.id).single();
    if (r?.role === 'admin') { setUser(data.user); fetchAll(); refreshCommunityPendingCount(); }
    else { await supabase.auth.signOut(); alert('Acesso não autorizado.'); }
    setLoading(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut(); localStorage.clear();
    window.location.href = window.location.origin + window.location.pathname + '?view=admin';
  };

  const getMatching = (base, scope) => {
    const bd = new Date(base.class_time);
    return lessons.filter(l => {
      const d = new Date(l.class_time);
      const ok = l.title === base.title && l.teacher_name === base.teacher_name &&
        l.level === base.level && d.getUTCDay() === bd.getUTCDay() &&
        d.getUTCHours() === bd.getUTCHours() && d.getUTCMinutes() === bd.getUTCMinutes();
      if (!ok) return false;
      if (scope === 'all') return true;
      if (scope === 'future') return d >= bd;
      return l.id === base.id;
    });
  };

  const openAdd = () => {
    setModalMode('add');
    setFormData({ title: 'Individual lesson', teacher_name: 'Victor', level: 'Beginner', date: '', time: '09:00', timezone: 'America/Sao_Paulo', recurrence: 'none', recurrenceEnd: '', meet_link: '' });
    setIsModalOpen(true);
  };

  const openEdit = (lesson) => {
    setModalMode('edit'); setSelectedLessonId(lesson.id);
    setSelectedLessonOriginal(lesson); setEditBulk(false);
    const d = new Date(lesson.class_time);
    setFormData({ title: lesson.title, teacher_name: lesson.teacher_name, level: lesson.level,
      date: d.toISOString().split('T')[0], time: d.toISOString().split('T')[1].substring(0, 5), timezone: lesson.timezone || 'America/Sao_Paulo',
      recurrence: 'none', recurrenceEnd: '', meet_link: lesson.meet_link || '' });
    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.timezone) { alert('Selecione um fuso horário antes de salvar.'); return; }
    setLoading(true);
    if (modalMode === 'edit') {
      if (editBulk && selectedLessonOriginal) {
        const matches = getMatching(selectedLessonOriginal, 'all');
        for (let match of matches) {
          const matchDateStr = new Date(match.class_time).toISOString().split('T')[0];
          const newUtc = zonedTimeToUtc(matchDateStr, formData.time, formData.timezone);
          await supabase.from('lessons').update({ title: formData.title, teacher_name: formData.teacher_name, level: formData.level, class_time: newUtc.toISOString(), timezone: formData.timezone, meet_link: formData.meet_link || null }).eq('id', match.id);
        }
      } else {
        const newUtc = zonedTimeToUtc(formData.date, formData.time, formData.timezone);
        await supabase.from('lessons').update({ title: formData.title, teacher_name: formData.teacher_name, level: formData.level, class_time: newUtc.toISOString(), timezone: formData.timezone, meet_link: formData.meet_link || null }).eq('id', selectedLessonId);
      }
    } else {
      const rows = [];
      let currDate = new Date(`${formData.date}T00:00:00Z`); // tracks the wall-clock calendar date only, advanced week by week
      const endDate = formData.recurrence === 'weekly' && formData.recurrenceEnd ? new Date(`${formData.recurrenceEnd}T23:59:59Z`) : currDate;
      while (currDate <= endDate) {
        const dateStr = currDate.toISOString().split('T')[0];
        const utcTime = zonedTimeToUtc(dateStr, formData.time, formData.timezone);
        rows.push({ title: formData.title, teacher_name: formData.teacher_name, level: formData.level, class_time: utcTime.toISOString(), timezone: formData.timezone, duration_minutes: 60, meet_link: formData.meet_link || null });
        if (formData.recurrence === 'none') break;
        currDate.setUTCDate(currDate.getUTCDate() + 7);
      }
      await supabase.from('lessons').insert(rows);
    }
    setIsModalOpen(false); fetchAll(); setLoading(false);
  };

  const confirmDelete = async () => {
    setLoading(true);
    const ids = getMatching(lessonToDelete, deleteScope).map(m => m.id);
    if (ids.length > 0) {
      await Promise.all([
        supabase.from('signups').delete().in('lesson_id', ids),
        supabase.from('waitlist').delete().in('lesson_id', ids),
        supabase.from('attendance').delete().in('lesson_id', ids),
        supabase.from('lesson_materials').delete().in('lesson_id', ids),
      ]);
      await supabase.from('lessons').delete().in('id', ids);
    }
    setDeleteModalOpen(false); setLessonToDelete(null); fetchAll(); setLoading(false);
  };

  const openAttModal = (lesson) => {
    setAttLesson(lesson);
    const existing = allAttendance.filter(a => a.lesson_id === lesson.id);
    const obj = {};
    lesson.enrolled.forEach(sig => {
      const rec = existing.find(a => a.student_email === sig.student_email);
      obj[sig.student_email] = { status: rec?.status || null, note: rec?.note || '' };
    });
    setAttData(obj);
    setMatData({ content_covered: lesson.materials?.content_covered || '', homework: lesson.materials?.homework || '', teacher_notes: lesson.materials?.teacher_notes || '' });
    setAttModalOpen(true);
  };

  const saveAttendance = async () => {
    setLoading(true);
    const records = Object.entries(attData).filter(([, d]) => d.status).map(([email, d]) => ({ lesson_id: attLesson.id, student_email: email, status: d.status, note: d.note || null }));
    if (records.length) await supabase.from('attendance').upsert(records, { onConflict: 'lesson_id,student_email' });
    if (matData.content_covered || matData.homework || matData.teacher_notes) {
      await supabase.from('lesson_materials').upsert({ lesson_id: attLesson.id, ...matData, updated_at: new Date().toISOString() }, { onConflict: 'lesson_id' });
    }
    setAttModalOpen(false); fetchAll(); setLoading(false);
  };

  const openProfile = (student) => { setSelStudent(student); setProfileNote(student.profileNote || ''); setProfileOpen(true); };

  const saveProfileNote = async () => {
    setProfileSaving(true);
    await supabase.from('student_profile_notes').upsert({ student_id: selStudent.id, note: profileNote, updated_at: new Date().toISOString(), updated_by: user?.email || 'admin' }, { onConflict: 'student_id' });
    setProfileSaving(false); fetchAll();
    setSelStudent(prev => ({ ...prev, profileNote: profileNote }));
  };

  const handleLevelChange = async (student, newLevel) => {
    if (student.level === newLevel) return;
    await supabase.from('students').update({ level: newLevel, ready_for_levelup: false }).eq('id', student.id);
    await supabase.from('level_progression').insert({ student_id: student.id, old_level: student.level, new_level: newLevel, changed_by: user?.email || 'admin' });
    fetchAll();
    if (selStudent?.id === student.id) setSelStudent(prev => ({ ...prev, level: newLevel }));
  };

  const handlePlanChange = async (student, newPlan) => {
    const todayStr = new Date().toISOString().split('T')[0];
    const newStart = newPlan !== student.plan ? todayStr : student.contract_start;
    const newEnd = calcContractEnd(newPlan, newStart);
    await supabase.from('students').update({ plan: newPlan, contract_start: newStart, contract_end: newEnd || null }).eq('id', student.id);
    fetchAll();
    if (selStudent?.id === student.id) setSelStudent(prev => ({ ...prev, plan: newPlan, contract_start: newStart, contract_end: newEnd }));
  };

  const toggleFlag = async (student, field) => {
    await supabase.from('students').update({ [field]: !student[field] }).eq('id', student.id);
    fetchAll();
    if (selStudent?.id === student.id) setSelStudent(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleAddStudent = async (e) => {
    e.preventDefault(); setLoading(true);
    const contractEnd = calcContractEnd(studentForm.plan, studentForm.contract_start);
    const { error } = await supabase.from('students').insert({
      full_name: studentForm.full_name, email: studentForm.email.toLowerCase().trim(),
      level: studentForm.level, plan: studentForm.plan,
      contract_start: studentForm.contract_start || null, contract_end: contractEnd || null,
      phone: studentForm.phone || null, is_trial: studentForm.is_trial, role: 'student', language: studentForm.language || 'english', ui_language: studentForm.ui_language || 'pt',
    });
    if (error) { alert('Erro: ' + error.message); setLoading(false); return; }
    setStudentModalOpen(false);
    setStudentForm({ full_name: '', email: '', level: 'Beginner', plan: 'kickstart', contract_start: '', phone: '', is_trial: false });
    fetchAll(); setLoading(false);
  };

  const openTrialPopup = (student) => { setTrialStudent(student); setTrialNote(student.profileNote || ''); setTrialPopupOpen(true); };

  const saveTrialNote = async () => {
    setTrialSaving(true);
    await supabase.from('student_profile_notes').upsert({ student_id: trialStudent.id, note: trialNote, updated_at: new Date().toISOString(), updated_by: user?.email || 'admin' }, { onConflict: 'student_id' });
    setTrialSaving(false); fetchAll();
  };

  const openContactModal = (student) => { setSelStudent(student); setContactForm({ email: student.email || '', phone: student.phone || '', full_name: student.full_name || '' }); setContactModalOpen(true); };

  const saveContact = async () => {
    setContactSaving(true);
    await supabase.from('students').update({ full_name: contactForm.full_name.trim() || selStudent.full_name, email: contactForm.email.toLowerCase().trim(), phone: contactForm.phone || null }).eq('id', selStudent.id);
    setContactSaving(false); setContactModalOpen(false); fetchAll();
  };

  const deleteStudent = async (student) => {
    if (!window.confirm(`Excluir ${student.full_name} permanentemente? Esta ação não pode ser desfeita.`)) return;
    setLoading(true);
    await supabase.from('signups').delete().eq('student_email', student.email);
    await supabase.from('waitlist').delete().eq('student_email', student.email);
    await supabase.from('attendance').delete().eq('student_email', student.email);
    await supabase.from('cancellations').delete().eq('student_email', student.email);
    await supabase.from('reflections').delete().eq('student_id', student.id);
    await supabase.from('checkins').delete().eq('student_id', student.id);
    await supabase.from('student_profile_notes').delete().eq('student_id', student.id);
    await supabase.from('level_progression').delete().eq('student_id', student.id);
    await supabase.from('students').delete().eq('id', student.id);
    setProfileOpen(false); fetchAll(); setLoading(false);
  };

  // Cancels a student's contract WITHOUT deleting their profile, attendance history,
  // reflections, or anything else — sets contract_status to 'cancelled' (a value the
  // rest of the console already reads and displays, e.g. the "❌ Cancelado" badge and
  // the at-risk calculation), and logs the cancellation with a reason for the student's
  // history. The student remains fully visible and searchable, just marked inactive.
  const cancelContract = async (student) => {
    if (student.contract_status === 'cancelled') { alert('Este aluno já está com o contrato cancelado.'); return; }
    const reason = window.prompt(`Cancelar o contrato de ${student.full_name}?\n\nMotivo (opcional):`);
    if (reason === null) return; // user hit "cancel" on the prompt itself, abort
    setLoading(true);
    await supabase.from('cancellations').insert({
      student_email: student.email,
      reason: reason || null,
      cancelled_at: new Date().toISOString(),
    });
    await supabase.from('students').update({ contract_status: 'cancelled' }).eq('id', student.id);
    setProfileOpen(false); fetchAll(); setLoading(false);
  };

  // Undoes a cancellation — sets contract_status back to active (null), so the
  // student's badges/at-risk logic return to normal. Does not remove the historical
  // cancellation record, since that's a legitimate part of the student's history.
  const reactivateContract = async (student) => {
    if (!window.confirm(`Reativar o contrato de ${student.full_name}?`)) return;
    setLoading(true);
    await supabase.from('students').update({ contract_status: null }).eq('id', student.id);
    setProfileOpen(false); fetchAll(); setLoading(false);
  };

  const getStudentName = (email) => students.find(s => s.email === email)?.full_name || email;

  const wkStart = new Date(now); wkStart.setDate(now.getDate() - ((now.getDay() + 6) % 7)); wkStart.setHours(0,0,0,0);
  const wkEnd   = new Date(wkStart); wkEnd.setDate(wkStart.getDate() + 6); wkEnd.setHours(23,59,59,999);
  const classesThisWeek = lessons.filter(l => { const d = new Date(l.class_time); return d >= wkStart && d <= wkEnd; }).length;

  const thisMonthLessons = lessons.filter(l => new Date(l.class_time) >= monthStart);
  const avgEnrolledOverall = thisMonthLessons.length > 0
    ? (thisMonthLessons.reduce((a, l) => a + l.enrolled.length, 0) / thisMonthLessons.length).toFixed(1)
    : '—';

  const atRisk     = students.filter(s => s.isAtRisk);
  const trials     = students.filter(s => s.is_trial);
  const levelReady = students.filter(s => s.ready_for_levelup);
  const ratedStudents = students.filter(s => s.totalMarked > 0);
  const avgAtt = ratedStudents.length ? Math.round(ratedStudents.reduce((a, s) => a + s.attRate, 0) / ratedStudents.length) : 0;
  const expiringContracts = students.filter(s => { const d = daysLeft(s.contract_end); return d !== null && d >= 0 && d <= 60; }).sort((a,b) => daysLeft(a.contract_end) - daysLeft(b.contract_end));

  
  const filteredLessons = lessons.filter(l =>
    (!teacherFilter || l.teacher_name === teacherFilter) &&
    (!typeFilter    || l.title === typeFilter) &&
    (!dateFilter    || l.class_time.startsWith(dateFilter)) &&
    (showPast || new Date(l.class_time) >= new Date())
  ).sort((a, b) => sortOrder === 'asc' ? new Date(a.class_time) - new Date(b.class_time) : new Date(b.class_time) - new Date(a.class_time));

  const filteredStudents = students.filter(s =>
    (!studentSearch || s.full_name.toLowerCase().includes(studentSearch.toLowerCase()) || s.email.toLowerCase().includes(studentSearch.toLowerCase())) &&
    (!planFilter  || s.plan === planFilter) &&
    (!levelFilter || s.level === levelFilter)
  );

  const uniqueTeachers = [...new Set(lessons.map(l => l.teacher_name))].filter(Boolean);
  const uniqueTypes    = [...new Set(lessons.map(l => l.title))].filter(Boolean);
  const levelColor = { Beginner: { bg: '#dbeafe', color: '#1d4ed8' }, Intermediate: { bg: '#ede9fe', color: '#7c3aed' } };
  const levelCapacity = (title) => title === 'Individual lesson' || title === 'Elite Squad Individual' ? 1 : title === 'Plantão de dúvidas' ? 9999 : 6;

  const STATS = [
    { label: 'Total de Alunos', value: students.length, color: '#1a2a6c', icon: '👥', info: 'Número total de alunos cadastrados na plataforma.' },
    { label: 'Aulas esta Semana', value: classesThisWeek, color: '#2563eb', icon: '📅', info: 'Total de aulas agendadas entre segunda e domingo desta semana.' },
    { label: 'Taxa de Presença', value: ratedStudents.length ? `${avgAtt}%` : '—', color: '#16a34a', icon: '✅', info: 'Média de presença dos alunos com pelo menos uma aula registrada. Aparece somente após marcar presença em uma aula via o botão 📋 Marcar.' },
    { label: 'Média Inscritos', value: avgEnrolledOverall, color: '#0d9488', icon: '📊', info: `Média de alunos inscritos por aula — calculado a partir de ${monthStart.toLocaleDateString('pt-BR')} para evitar distorção histórica.` },
    { label: 'Em Risco', value: atRisk.length, color: atRisk.length > 0 ? '#dc2626' : '#94a3b8', icon: '⚠️', info: 'Sem aulas futuras agendadas e último agendamento há +14 dias. Risco de evasão.' },
    { label: 'Contratos Expirando', value: expiringContracts.length, color: expiringContracts.length > 0 ? '#dc2626' : '#94a3b8', icon: '📋', info: 'Alunos com contrato vencendo nos próximos 60 dias. Veja Relatórios para detalhes.' },
  ];

  if (!user) return (
    <>
      <style>{adminStyles}</style>
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #0f172a 0%, #1a2a6c 60%, #ff6a00 100%)' }}>
        <form onSubmit={handleLogin} style={{ background: 'white', padding: '52px 44px', borderRadius: '24px', width: '420px', boxShadow: '0 40px 80px rgba(0,0,0,0.35)', textAlign: 'center' }}>
          <div style={{ background: 'linear-gradient(135deg, #1a2a6c, #ff6a00)', width: '68px', height: '68px', borderRadius: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 28px', color: 'white', fontWeight: '900', fontSize: '2rem' }}>Q</div>
          <h2 style={{ color: '#0f172a', fontWeight: '900', fontSize: '1.7rem', marginBottom: '6px' }}>Admin Access</h2>
          <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '36px' }}>Idiomas Quevedo — Equipe</p>
          <input type="email" placeholder="E-mail" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} className="filter-input" style={{ width: '100%', marginBottom: '12px', padding: '15px 16px' }} required />
          <input type="password" placeholder="Senha" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} className="filter-input" style={{ width: '100%', marginBottom: '28px', padding: '15px 16px' }} required />
          <button type="submit" disabled={loading} style={{ width: '100%', padding: '16px', background: 'linear-gradient(135deg, #1a2a6c, #2563eb)', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '800', fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'Raleway' }}>
            {loading ? 'VERIFICANDO...' : 'ENTRAR NO DASHBOARD'}
          </button>
        </form>
      </div>
    </>
  );

  return (
    <>
      <style>{adminStyles}</style>
      <div style={{ minHeight: '100vh', background: '#f0f4f8' }}>

        {/* Header */}
        <div style={{ background: 'white', borderBottom: '1px solid #e2e8f0', padding: '0 32px', position: 'sticky', top: 0, zIndex: 100 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '68px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <div style={{ background: 'linear-gradient(135deg, #1a2a6c, #ff6a00)', width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '900', fontSize: '1.2rem' }}>Q</div>
              <div>
                <div style={{ fontWeight: '800', color: '#0f172a', lineHeight: 1.2 }}>Idiomas Quevedo</div>
                <div style={{ fontSize: '0.68rem', color: '#ff6a00', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1.5px' }}>Management Console</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '2px', background: '#f1f5f9', padding: '4px', borderRadius: '12px' }}>
              {[['lessons','📅','Aulas'],['students','👥','Alunos'],['reports','📊','Relatórios'],['community','🌎','Comunidade']].map(([tab,icon,label]) => (
                <button key={tab} onClick={() => setActiveTab(tab)} style={{ position: 'relative', background: activeTab===tab ? '#1a2a6c' : 'transparent', color: activeTab===tab ? 'white' : '#64748b', border: 'none', padding: '8px 18px', borderRadius: '9px', fontWeight: '800', cursor: 'pointer', fontSize: '0.82rem', fontFamily: 'Raleway', transition: 'all 0.2s' }}>
                  {icon} {label}
                  {tab === 'community' && communityPendingCount > 0 && (
                    <span style={{ position: 'absolute', top: '-6px', right: '-6px', background: '#dc2626', color: 'white', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: '900' }}>{communityPendingCount}</span>
                  )}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              {activeTab === 'lessons' && <button onClick={openAdd} style={{ background: '#ff6a00', color: 'white', border: 'none', padding: '9px 18px', borderRadius: '10px', fontWeight: '800', cursor: 'pointer', fontSize: '0.82rem', fontFamily: 'Raleway' }}>+ NOVA AULA</button>}
              <button onClick={handleLogout} style={{ background: 'white', color: '#ef4444', border: '1.5px solid #ef4444', padding: '8px 16px', borderRadius: '10px', fontWeight: '700', cursor: 'pointer', fontSize: '0.82rem', fontFamily: 'Raleway' }}>SAIR</button>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div style={{ display: 'flex', gap: '14px', padding: '20px 32px', flexWrap: 'wrap' }}>
          {STATS.map(({ label, value, color, icon, info }) => (
            <div key={label} className="stat-card" style={{ borderTop: `4px solid ${color}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ fontSize: '1.8rem', fontWeight: '900', color, lineHeight: 1 }}>{value}</div>
                <button className="info-btn" onMouseEnter={() => setTooltipVisible(label)} onMouseLeave={() => setTooltipVisible(null)} onClick={() => setTooltipVisible(tooltipVisible === label ? null : label)}>?</button>
              </div>
              <div style={{ fontSize: '0.72rem', fontWeight: '700', color: '#94a3b8', marginTop: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{icon} {label}</div>
              {tooltipVisible === label && (
                <div className="tooltip-box">
                  <div className="tooltip-arrow-up" />
                  {info}
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{ padding: '0 32px 120px' }}>

          {/* AULAS TAB */}
          {activeTab === 'lessons' && (
            <>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap', background: 'white', padding: '16px', borderRadius: '14px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <select className="filter-input" value={teacherFilter} onChange={e => setTeacherFilter(e.target.value)} style={{ flex: 1, minWidth: '150px' }}>
                  <option value="">👨‍🏫 Todos os Professores</option>
                  {uniqueTeachers.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <select className="filter-input" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{ flex: 1, minWidth: '150px' }}>
                  <option value="">📚 Todos os Tipos</option>
                  {uniqueTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <input type="date" className="filter-input" value={dateFilter} onChange={e => setDateFilter(e.target.value)} />
                <button onClick={() => setSortOrder(p => p === 'asc' ? 'desc' : 'asc')} className="filter-input" style={{ cursor: 'pointer', fontFamily: 'Raleway', fontWeight: '700' }}>
                  {sortOrder === 'asc' ? '⬆ Antigas → Novas' : '⬇ Novas → Antigas'}
                </button>
                <button onClick={() => setShowPast(p => !p)} className="filter-input" style={{ cursor: 'pointer', fontFamily: 'Raleway', fontWeight: '700', background: showPast ? '#fee2e2' : 'white', color: showPast ? '#dc2626' : '#64748b' }}>
                  {showPast ? '🕐 Mostrando todas' : '📅 A partir de hoje'}
                </button>
                {(teacherFilter || typeFilter || dateFilter) && (
                  <button onClick={() => { setTeacherFilter(''); setTypeFilter(''); setDateFilter(''); }} style={{ background: '#ff6a00', color: 'white', border: 'none', borderRadius: '10px', padding: '10px 16px', fontWeight: '800', cursor: 'pointer', fontSize: '0.82rem', fontFamily: 'Raleway' }}>Limpar</button>
                )}
              </div>
              <div className="card">
                <div className="table-scroll">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Aula / Professor</th><th>Data & Hora <span style={{ fontWeight: '500', fontSize: '0.7rem', color: '#94a3b8', textTransform: 'none' }}>(horário de Brasília, GMT-3)</span></th><th>Vagas</th><th>Lista de Espera</th><th>Meet Link</th><th>Presença</th><th style={{ textAlign: 'right' }}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLessons.map(l => {
                        const cap = levelCapacity(l.title); const capLabel = cap === 9999 ? '∞' : cap;
                        const isFull = l.enrolled.length >= cap; const isPast = new Date(l.class_time) < now;
                        return (
                          <tr key={l.id}>
                            <td>
                              <div style={{ fontWeight: '800', color: '#0f172a', marginBottom: '4px' }}>{l.title}</div>
                              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                                <span style={{ fontSize: '0.78rem', color: '#64748b' }}>Prof. {l.teacher_name}</span>
                                <span className="badge" style={{ background: (levelColor[l.level]||{bg:'#f1f5f9'}).bg, color: (levelColor[l.level]||{color:'#475569'}).color }}>{l.level}</span>
                              </div>
                            </td>
                            <td>
                              <div style={{ fontWeight: '700', color: '#1a2a6c', fontSize: '0.9rem' }}>{new Date(l.class_time).toLocaleString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' })}</div>
                              {isPast && <span className="badge" style={{ background: '#f1f5f9', color: '#94a3b8', marginTop: '4px' }}>Passada</span>}
                            </td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: isFull ? '#dc2626' : '#16a34a', flexShrink: 0 }} />
                                <strong>{l.enrolled.length} / {capLabel}</strong>
                              </div>
                              <div style={{ fontSize: '0.72rem', color: '#94a3b8', maxWidth: '220px', lineHeight: '1.5' }}>
                                {l.enrolled.map(s => getStudentName(s.student_email)).join(', ') || <em>Nenhum inscrito</em>}
                              </div>
                            </td>
                            <td>{l.waiting.length > 0 ? <span className="badge" style={{ background: '#fff7ed', color: '#ea580c' }}>⏳ {l.waiting.length}</span> : <span style={{ color: '#cbd5e1', fontSize: '0.8rem' }}>—</span>}</td>
                            <td>{l.meet_link ? <a href={l.meet_link} target="_blank" rel="noopener noreferrer" className="badge" style={{ background: '#dcfce7', color: '#166534', textDecoration: 'none' }}>🎥 Abrir Meet</a> : <span style={{ color: '#cbd5e1', fontSize: '0.8rem' }}>Sem link</span>}</td>
                            <td>{l.enrolled.length > 0 ? <button onClick={() => openAttModal(l)} className="btn-action" style={{ background: l.attendanceMarked ? '#dcfce7' : '#fff7ed', color: l.attendanceMarked ? '#166534' : '#ea580c', borderColor: l.attendanceMarked ? '#86efac' : '#fed7aa' }}>{l.attendanceMarked ? '✅ Ver' : '📋 Marcar'}</button> : <span style={{ color: '#cbd5e1', fontSize: '0.8rem' }}>—</span>}</td>
                            <td style={{ textAlign: 'right' }}>
                              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                <button onClick={() => openEdit(l)} className="btn-action" style={{ background: '#f8fafc', color: '#1a2a6c', borderColor: '#e2e8f0' }}>Editar</button>
                                <button onClick={() => { setLessonToDelete(l); setDeleteScope('single'); setDeleteModalOpen(true); }} className="btn-action" style={{ background: 'white', color: '#dc2626', borderColor: '#fecaca' }}>Excluir</button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {filteredLessons.length === 0 && <tr><td colSpan="7" style={{ textAlign: 'center', padding: '50px', color: '#94a3b8', fontWeight: '600' }}>Nenhuma aula encontrada</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* ALUNOS TAB */}
          {activeTab === 'students' && (
            <>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap', background: 'white', padding: '16px', borderRadius: '14px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <input type="text" placeholder="🔍 Buscar por nome ou e-mail..." value={studentSearch} onChange={e => setStudentSearch(e.target.value)} className="filter-input" style={{ flex: 2, minWidth: '200px' }} />
                <select className="filter-input" value={planFilter} onChange={e => setPlanFilter(e.target.value)} style={{ flex: 1, minWidth: '140px' }}>
                  <option value="">📋 Todos os Planos</option>
                  {Object.entries(PLAN_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                </select>
                <select className="filter-input" value={levelFilter} onChange={e => setLevelFilter(e.target.value)} style={{ flex: 1, minWidth: '130px' }}>
                  <option value="">🎓 Todos os Níveis</option>
                  {LEVELS.map(lv => <option key={lv} value={lv}>{lv}</option>)}
                </select>
                {(studentSearch || planFilter || levelFilter) && (
                  <button onClick={() => { setStudentSearch(''); setPlanFilter(''); setLevelFilter(''); }} style={{ background: '#ff6a00', color: 'white', border: 'none', borderRadius: '10px', padding: '10px 16px', fontWeight: '800', cursor: 'pointer', fontSize: '0.82rem', fontFamily: 'Raleway' }}>Limpar</button>
                )}
                <span style={{ fontSize: '0.85rem', color: '#94a3b8', fontWeight: '600', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>{filteredStudents.length} aluno{filteredStudents.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="card">
                <div className="table-scroll">
                  <table className="admin-table">
                    <thead>
                      <tr><th>Aluno</th><th>Nível</th><th>Plano / Curso</th><th>Contrato</th><th>Presença</th><th>Status</th><th style={{ textAlign: 'right' }}>Ações</th></tr>
                    </thead>
                    <tbody>
                      {filteredStudents.map(s => {
                        const dl = daysLeft(s.contract_end);
                        return (
                          <tr key={s.id}>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: 'linear-gradient(135deg, #1a2a6c, #2563eb)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '1rem', flexShrink: 0 }}>
                                  {s.full_name.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <div style={{ fontWeight: '700', color: '#0f172a' }}>{s.full_name}</div>
                                  <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{s.email}</div>
                                <div style={{ fontSize: '0.72rem', marginTop: '2px' }}>{s.language === 'spanish' ? '🇪🇸 Español' : '🇧🇷 English'}</div>
                                  {s.phone && <div style={{ fontSize: '0.72rem', color: '#64748b' }}>📞 {s.phone}</div>}
                                </div>
                              </div>
                            </td>
                            <td>
                              <select value={s.level} onChange={e => handleLevelChange(s, e.target.value)}
                                style={{ padding: '7px 12px', borderRadius: '8px', border: '1.5px solid #e2e8f0', outline: 'none', fontWeight: '800', fontFamily: 'Raleway', cursor: 'pointer', background: (levelColor[s.level]||{}).bg, color: (levelColor[s.level]||{}).color, fontSize: '0.82rem' }}>
                                {LEVELS.map(lv => <option key={lv} value={lv}>{lv}</option>)}
                              </select>
                            </td>
                            <td>
                              <select value={s.plan||'kickstart'} onChange={e => handlePlanChange(s, e.target.value)}
                                style={{ padding: '7px 12px', borderRadius: '8px', border: '1.5px solid #e2e8f0', outline: 'none', fontWeight: '800', fontFamily: 'Raleway', cursor: 'pointer', background: (PLAN_COLORS[s.plan]||{}).bg, color: (PLAN_COLORS[s.plan]||{}).color, fontSize: '0.82rem' }}>
                                {/* Legacy plan stays selectable only if the student is currently on it, so it keeps displaying correctly without being offered to anyone else */}
                                {!SELLABLE_PLANS.includes(s.plan) && s.plan && <option value={s.plan}>{PLAN_LABELS[s.plan] || s.plan} (legado)</option>}
                                {SELLABLE_PLANS.map(key => <option key={key} value={key}>{PLAN_LABELS[key]}</option>)}
                              </select>
                            </td>
                            <td>
                              {s.contract_start ? (
                                <div>
                                  <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '4px' }}>
                                    {new Date(s.contract_start).toLocaleDateString('pt-BR',{timeZone:'UTC'})} → {s.contract_end ? new Date(s.contract_end).toLocaleDateString('pt-BR',{timeZone:'UTC'}) : '—'}
                                  </div>
                                  {dl !== null && (
                                    <span className="badge" style={{ background: expiryStyle(dl).bg, color: expiryStyle(dl).color }}>
                                      {dl < 0 ? 'Expirado' : dl === 0 ? 'Hoje' : `${dl}d`}
                                    </span>
                                  )}
                                </div>
                              ) : <span style={{ color: '#cbd5e1', fontSize: '0.8rem' }}>Sem contrato</span>}
                            </td>
                            <td>
                              {s.totalMarked > 0 ? (
                                <div>
                                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px' }}>
                                    <div style={{ flex: 1, height: '6px', background: '#f1f5f9', borderRadius: '3px', overflow: 'hidden', maxWidth: '80px' }}>
                                      <div style={{ width: `${s.attRate}%`, height: '100%', background: s.attRate >= 80 ? '#16a34a' : s.attRate >= 60 ? '#d97706' : '#dc2626', borderRadius: '3px' }} />
                                    </div>
                                    <span style={{ fontWeight: '800', fontSize: '0.82rem', color: s.attRate >= 80 ? '#16a34a' : s.attRate >= 60 ? '#d97706' : '#dc2626' }}>{s.attRate}%</span>
                                  </div>
                                  <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{s.totalAttended}/{s.totalMarked}</div>
                                </div>
                              ) : <span style={{ color: '#cbd5e1', fontSize: '0.8rem' }}>—</span>}
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                {s.contract_status === 'cancelled' && <span className="badge" style={{ background: '#1e293b', color: 'white' }}>❌ Cancelado</span>}
                            {s.contract_status !== 'cancelled' && s.isAtRisk && <span className="badge" style={{ background: '#fee2e2', color: '#dc2626' }}>⚠️ Risco</span>}
                                {s.is_trial && (
                                  <button onClick={() => openTrialPopup(s)} className="badge" style={{ background: '#ede9fe', color: '#7c3aed', border: 'none', cursor: 'pointer' }}>🆕 Trial ℹ</button>
                                )}
                                {s.ready_for_levelup && <span className="badge" style={{ background: '#fef3c7', color: '#d97706' }}>📈 Subir</span>}
                                {!s.isAtRisk && !s.is_trial && !s.ready_for_levelup && <span style={{ color: '#cbd5e1', fontSize: '0.8rem' }}>—</span>}
                              </div>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                <button onClick={() => openContactModal(s)} className="btn-action" style={{ background: '#f0f9ff', color: '#0284c7', borderColor: '#bae6fd', fontSize: '0.72rem' }}>✏️ Contato</button>
                                <button onClick={() => toggleFlag(s, 'is_trial')} className="btn-action" style={{ background: s.is_trial ? '#ede9fe' : 'white', color: '#7c3aed', borderColor: '#ddd6fe', fontSize: '0.72rem' }}>{s.is_trial ? '✓ Trial' : '+ Trial'}</button>
                                <button onClick={() => toggleFlag(s, 'ready_for_levelup')} className="btn-action" style={{ background: s.ready_for_levelup ? '#fef3c7' : 'white', color: '#d97706', borderColor: '#fde68a', fontSize: '0.72rem' }}>{s.ready_for_levelup ? '✓' : '+ Subir'}</button>
                                <button onClick={() => openProfile(s)} className="btn-action" style={{ background: '#1a2a6c', color: 'white', borderColor: '#1a2a6c' }}>Perfil</button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* RELATÓRIOS TAB */}
          {activeTab === 'reports' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div className="card">
                <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h3 style={{ margin: 0, fontWeight: '800', color: '#dc2626', fontSize: '1rem' }}>📋 Contratos Expirando em 60 dias</h3>
                    <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#94a3b8' }}>Oportunidades de renovação — entre em contato proativamente</p>
                  </div>
                  <span className="badge" style={{ background: '#fee2e2', color: '#dc2626' }}>{expiringContracts.length}</span>
                </div>
                {expiringContracts.length === 0
                  ? <div style={{ padding: '32px', textAlign: 'center', color: '#94a3b8' }}>✅ Nenhum contrato expirando nos próximos 60 dias</div>
                  : <div className="table-scroll">
                      <table className="admin-table">
                        <thead><tr><th>Aluno</th><th>Plano</th><th>Expira em</th><th>Ação</th></tr></thead>
                        <tbody>
                          {expiringContracts.map(s => {
                            const dl = daysLeft(s.contract_end);
                            return (
                              <tr key={s.id}>
                                <td><div style={{ fontWeight: '700' }}>{s.full_name}</div><div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>{s.email}</div></td>
                                <td><span className="badge" style={{ background: (PLAN_COLORS[s.plan]||{}).bg, color: (PLAN_COLORS[s.plan]||{}).color }}>{PLAN_LABELS[s.plan]}</span></td>
                                <td><span className="badge" style={{ background: expiryStyle(dl).bg, color: expiryStyle(dl).color }}>{dl === 0 ? 'Hoje!' : `${dl} dias`} — {new Date(s.contract_end).toLocaleDateString('pt-BR',{timeZone:'UTC'})}</span></td>
                                <td>
                                  <a href={`https://api.whatsapp.com/send?phone=55${(s.phone||'').replace(/\D/g,'')}&text=${encodeURIComponent(`Olá ${s.full_name.split(' ')[0]}! Seu contrato com a Idiomas Quevedo expira em breve. Vamos renovar?`)}`} target="_blank" rel="noopener noreferrer" className="btn-action" style={{ background: '#dcfce7', color: '#166534', borderColor: '#86efac', textDecoration: 'none', display: 'inline-block' }}>💬 WhatsApp</a>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                }
              </div>

              <div className="card">
                <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <h3 style={{ margin: 0, fontWeight: '800', color: '#dc2626', fontSize: '1rem' }}>⚠️ Alunos em Risco de Evasão</h3>
                    <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#94a3b8' }}>Sem aulas agendadas e último agendamento há +14 dias</p>
                  </div>
                  <span className="badge" style={{ background: '#fee2e2', color: '#dc2626' }}>{atRisk.length}</span>
                </div>
                {atRisk.length === 0
                  ? <div style={{ padding: '32px', textAlign: 'center', color: '#94a3b8' }}>✅ Nenhum aluno em risco no momento</div>
                  : <div className="table-scroll">
                      <table className="admin-table">
                        <thead><tr><th>Aluno</th><th>Plano</th><th>Nível</th><th>Último Agendamento</th><th>Presença</th><th>Ação</th></tr></thead>
                        <tbody>
                          {atRisk.map(s => (
                            <tr key={s.id}>
                              <td><div style={{ fontWeight: '700' }}>{s.full_name}</div><div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>{s.email}</div></td>
                              <td><span className="badge" style={{ background: (PLAN_COLORS[s.plan]||{}).bg, color: (PLAN_COLORS[s.plan]||{}).color }}>{PLAN_LABELS[s.plan]}</span></td>
                              <td><span className="badge" style={{ background: (levelColor[s.level]||{}).bg, color: (levelColor[s.level]||{}).color }}>{s.level}</span></td>
                              <td style={{ color: '#dc2626', fontWeight: '700' }}>{s.lastBooking ? `${Math.floor((now-s.lastBooking)/(1000*60*60*24))} dias atrás` : 'Nunca'}</td>
                              <td>{s.attRate!==null ? `${s.attRate}%` : '—'}</td>
                              <td><a href={`mailto:${s.email}`} className="btn-action" style={{ background: '#ff6a00', color: 'white', borderColor: '#ff6a00', textDecoration: 'none', display: 'inline-block' }}>✉️ Contatar</a></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                }
              </div>

              {levelReady.length > 0 && (
                <div className="card">
                  <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9' }}>
                    <h3 style={{ margin: 0, fontWeight: '800', color: '#d97706', fontSize: '1rem' }}>📈 Prontos para Subir de Nível</h3>
                  </div>
                  <div className="table-scroll">
                    <table className="admin-table">
                      <thead><tr><th>Aluno</th><th>Nível Atual</th><th>Próximo</th><th>Ação</th></tr></thead>
                      <tbody>
                        {levelReady.map(s => {
                          const nextIdx = LEVELS.indexOf(s.level)+1; const next = LEVELS[nextIdx];
                          return (
                            <tr key={s.id}>
                              <td><div style={{ fontWeight: '700' }}>{s.full_name}</div></td>
                              <td><span className="badge" style={{ background: (levelColor[s.level]||{}).bg, color: (levelColor[s.level]||{}).color }}>{s.level}</span></td>
                              <td>{next ? <span className="badge" style={{ background: (levelColor[next]||{}).bg, color: (levelColor[next]||{}).color }}>{next}</span> : <em style={{ color: '#94a3b8' }}>Máximo</em>}</td>
                              <td>{next && <button onClick={() => handleLevelChange(s,next)} className="btn-action" style={{ background: '#dcfce7', color: '#166534', borderColor: '#86efac' }}>✓ Promover</button>}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {trials.length > 0 && (
                <div className="card">
                  <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9' }}>
                    <h3 style={{ margin: 0, fontWeight: '800', color: '#7c3aed', fontSize: '1rem' }}>🆕 Alunos Trial</h3>
                  </div>
                  <div className="table-scroll">
                    <table className="admin-table">
                      <thead><tr><th>Aluno</th><th>Plano</th><th>Nível</th><th>Assistidas</th><th>Presença</th><th>Anotações</th><th>Ação</th></tr></thead>
                      <tbody>
                        {trials.map(s => (
                          <tr key={s.id}>
                            <td><div style={{ fontWeight: '700' }}>{s.full_name}</div><div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>{s.email}</div></td>
                            <td><span className="badge" style={{ background: (PLAN_COLORS[s.plan]||{}).bg, color: (PLAN_COLORS[s.plan]||{}).color }}>{PLAN_LABELS[s.plan]}</span></td>
                            <td><span className="badge" style={{ background: (levelColor[s.level]||{}).bg, color: (levelColor[s.level]||{}).color }}>{s.level}</span></td>
                            <td>{s.totalAttended}</td>
                            <td>{s.attRate!==null ? `${s.attRate}%` : '—'}</td>
                            <td style={{ maxWidth: '200px' }}>
                              <div style={{ fontSize: '0.78rem', color: '#64748b', lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>{s.profileNote || <em style={{ color: '#cbd5e1' }}>Sem anotação</em>}</div>
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: '6px' }}>
                                <button onClick={() => openTrialPopup(s)} className="btn-action" style={{ background: '#ede9fe', color: '#7c3aed', borderColor: '#ddd6fe' }}>📝 Ver</button>
                                <button onClick={() => toggleFlag(s,'is_trial')} className="btn-action" style={{ background: '#dcfce7', color: '#166534', borderColor: '#86efac' }}>✓ Converter</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="card">
                <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9' }}>
                  <h3 style={{ margin: 0, fontWeight: '800', color: '#1a2a6c', fontSize: '1rem' }}>📊 Utilização de Aulas — este mês</h3>
                  <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#94a3b8' }}>Dados a partir de {monthStart.toLocaleDateString('pt-BR')}</p>
                </div>
                <div className="table-scroll">
                  <table className="admin-table">
                    <thead><tr><th>Tipo de Aula</th><th>Sessões</th><th>Média Inscritos</th><th>Ocupação</th></tr></thead>
                    <tbody>
                      {LESSON_TYPES.map(type => {
                        const typeLessons = thisMonthLessons.filter(l => l.title === type);
                        if (!typeLessons.length) return null;
                        const cap = levelCapacity(type);
                        const avg = typeLessons.reduce((a,l) => a+l.enrolled.length,0)/typeLessons.length;
                        const fill = cap===9999 ? null : Math.round((avg/cap)*100);
                        return (
                          <tr key={type}>
                            <td style={{ fontWeight: '700' }}>{type}</td>
                            <td>{typeLessons.length}</td>
                            <td>{avg.toFixed(1)} / {cap===9999?'∞':cap}</td>
                            <td>
                              {fill!==null ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                  <div style={{ flex: 1, height: '8px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden', maxWidth: '100px' }}>
                                    <div style={{ width: `${Math.min(fill,100)}%`, height: '100%', background: fill>=80?'#dc2626':fill>=50?'#d97706':'#16a34a', borderRadius: '4px' }} />
                                  </div>
                                  <span style={{ fontWeight: '800', fontSize: '0.85rem', color: fill>=80?'#dc2626':fill>=50?'#d97706':'#16a34a' }}>{fill}%</span>
                                </div>
                              ) : <span style={{ color: '#94a3b8' }}>Ilimitado</span>}
                            </td>
                          </tr>
                        );
                      }).filter(Boolean)}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                <div className="card" style={{ padding: '24px' }}>
                  <h3 style={{ margin: '0 0 16px', fontWeight: '800', color: '#1a2a6c', fontSize: '1rem' }}>💳 Por Plano</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {Object.entries(PLAN_LABELS).map(([key,label]) => {
                      const count = students.filter(s => s.plan===key).length;
                      const pct = students.length ? Math.round((count/students.length)*100) : 0;
                      return (
                        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span className="badge" style={{ background: (PLAN_COLORS[key]||{}).bg, color: (PLAN_COLORS[key]||{}).color, minWidth: '120px', justifyContent: 'center' }}>{label}</span>
                          <div style={{ flex: 1, height: '8px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: (PLAN_COLORS[key]||{}).color, borderRadius: '4px' }} />
                          </div>
                          <span style={{ fontWeight: '800', color: '#475569', fontSize: '0.85rem', minWidth: '30px' }}>{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="card" style={{ padding: '24px' }}>
                  <h3 style={{ margin: '0 0 16px', fontWeight: '800', color: '#1a2a6c', fontSize: '1rem' }}>🎓 Por Nível</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {LEVELS.map(lv => {
                      const count = students.filter(s => s.level===lv).length;
                      const pct = students.length ? Math.round((count/students.length)*100) : 0;
                      return (
                        <div key={lv} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span className="badge" style={{ background: (levelColor[lv]||{bg:'#f1f5f9'}).bg, color: (levelColor[lv]||{color:'#475569'}).color, minWidth: '120px', justifyContent: 'center' }}>{lv}</span>
                          <div style={{ flex: 1, height: '8px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: (levelColor[lv]||{color:'#94a3b8'}).color, borderRadius: '4px' }} />
                          </div>
                          <span style={{ fontWeight: '800', color: '#475569', fontSize: '0.85rem', minWidth: '30px' }}>{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* COMUNIDADE TAB */}
          {activeTab === 'community' && (
            <AdminCommunitySection onCountChange={setCommunityPendingCount} />
          )}
        </div>

        {activeTab === 'students' && (
          <button className="fab" onClick={() => setStudentModalOpen(true)}>
            <span style={{ fontSize: '2rem', lineHeight: 1 }}>+</span> NOVO ALUNO
          </button>
        )}
      </div>

      {/* LESSON CRUD MODAL */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={e => e.target.className==='modal-overlay' && setIsModalOpen(false)}>
          <div className="modal-box" style={{ maxWidth: '520px', padding: '36px' }}>
            <button className="modal-close" onClick={() => setIsModalOpen(false)}>✕</button>
            <h2 style={{ margin: '0 0 24px', fontWeight: '800', color: '#0f172a', paddingRight: '40px' }}>{modalMode==='add' ? '+ Nova Aula' : '✏️ Editar Aula'}</h2>
            <form onSubmit={handleSave}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                <div>
                  <label style={{ fontSize: '0.75rem', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Tipo de Aula</label>
                  <select value={formData.title} onChange={e => setFormData({...formData,title:e.target.value})} className="filter-input" style={{ width:'100%',marginTop:'6px' }}>
                    {LESSON_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Nível</label>
                  <select value={formData.level} onChange={e => setFormData({...formData,level:e.target.value})} className="filter-input" style={{ width:'100%',marginTop:'6px' }}>
                    {LEVELS.map(lv => <option key={lv} value={lv}>{lv}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ fontSize: '0.75rem', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Professor</label>
                <select value={formData.teacher_name} onChange={e => setFormData({...formData,teacher_name:e.target.value})} className="filter-input" style={{ width:'100%',marginTop:'6px' }} required>
                  {TEACHERS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                <div>
                  <label style={{ fontSize: '0.75rem', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Data</label>
                  <input type="date" value={formData.date} onChange={e => setFormData({...formData,date:e.target.value})} className="filter-input" style={{ width:'100%',marginTop:'6px' }} required />
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Hora</label>
                  <input type="time" value={formData.time} onChange={e => setFormData({...formData,time:e.target.value})} className="filter-input" style={{ width:'100%',marginTop:'6px' }} required />
                </div>
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ fontSize: '0.75rem', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🌐 Fuso horário (a data/hora acima é NESTE fuso)</label>
                <select value={formData.timezone} onChange={e => setFormData({...formData,timezone:e.target.value})} className="filter-input" style={{ width:'100%',marginTop:'6px' }} required>
                  <option value="" disabled>Escolha o fuso horário...</option>
                  {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                </select>
                <p style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '4px', marginBottom: 0 }}>Cada aluno verá esta aula automaticamente convertida para o fuso horário do próprio navegador.</p>
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ fontSize: '0.75rem', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🎥 Google Meet Link</label>
                <input type="url" placeholder="https://meet.google.com/xxx-xxxx-xxx" value={formData.meet_link} onChange={e => setFormData({...formData,meet_link:e.target.value})} className="filter-input" style={{ width:'100%',marginTop:'6px' }} />
              </div>
              {modalMode==='edit' && (
                <div style={{ marginBottom:'14px',background:'#f8fafc',borderRadius:'10px',padding:'14px' }}>
                  <label style={{ display:'flex',alignItems:'center',gap:'10px',cursor:'pointer',fontWeight:'700',color:'#1a2a6c',fontSize:'0.88rem' }}>
                    <input type="checkbox" checked={editBulk} onChange={e => setEditBulk(e.target.checked)} style={{ transform:'scale(1.2)' }} />
                    Modificar todos os eventos da série
                  </label>
                  {editBulk && <p style={{ margin:'8px 0 0',fontSize:'0.75rem',color:'#d97706' }}>⚠️ Atualiza todas as aulas com as mesmas características, incluindo o Meet Link.</p>}
                </div>
              )}
              {modalMode==='add' && (
                <div style={{ marginBottom:'20px',background:'#f8fafc',borderRadius:'10px',padding:'14px' }}>
                  <label style={{ fontSize:'0.75rem',fontWeight:'800',color:'#64748b',textTransform:'uppercase',letterSpacing:'0.5px' }}>Recorrência</label>
                  <select value={formData.recurrence} onChange={e => setFormData({...formData,recurrence:e.target.value})} className="filter-input" style={{ width:'100%',marginTop:'6px' }}>
                    <option value="none">Aula única</option>
                    <option value="weekly">Semanal (até uma data)</option>
                  </select>
                  {formData.recurrence==='weekly' && (
                    <div style={{ marginTop:'10px' }}>
                      <label style={{ fontSize:'0.75rem',fontWeight:'800',color:'#64748b',textTransform:'uppercase',letterSpacing:'0.5px' }}>Repetir até</label>
                      <input type="date" value={formData.recurrenceEnd} onChange={e => setFormData({...formData,recurrenceEnd:e.target.value})} className="filter-input" style={{ width:'100%',marginTop:'6px' }} required />
                    </div>
                  )}
                </div>
              )}
              <div style={{ display:'flex',gap:'10px' }}>
                <button type="button" onClick={() => setIsModalOpen(false)} style={{ flex:1,padding:'14px',background:'white',color:'#64748b',border:'1.5px solid #e2e8f0',borderRadius:'12px',fontWeight:'700',cursor:'pointer',fontFamily:'Raleway' }}>Cancelar</button>
                <button type="submit" disabled={loading} style={{ flex:2,padding:'14px',background:'linear-gradient(135deg,#ff6a00,#ff8c00)',color:'white',border:'none',borderRadius:'12px',fontWeight:'800',cursor:'pointer',fontFamily:'Raleway' }}>{loading?'Salvando...':'Salvar Aula'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* STUDENT ONBOARDING MODAL */}
      {studentModalOpen && (
        <div className="modal-overlay" onClick={e => e.target.className==='modal-overlay' && setStudentModalOpen(false)}>
          <div className="modal-box" style={{ maxWidth:'520px',padding:'36px' }}>
            <button className="modal-close" onClick={() => setStudentModalOpen(false)}>✕</button>
            <h2 style={{ margin:'0 0 6px',fontWeight:'800',color:'#0f172a',paddingRight:'40px' }}>👤 Novo Aluno</h2>
            <p style={{ color:'#94a3b8',fontSize:'0.82rem',marginBottom:'24px' }}>A data de término do contrato é calculada automaticamente.</p>
            <form onSubmit={handleAddStudent}>
              <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px',marginBottom:'14px' }}>
                <div style={{ gridColumn:'span 2' }}>
                  <label style={{ fontSize:'0.75rem',fontWeight:'800',color:'#64748b',textTransform:'uppercase',letterSpacing:'0.5px' }}>Nome Completo</label>
                  <input type="text" placeholder="Nome do aluno" value={studentForm.full_name} onChange={e => setStudentForm({...studentForm,full_name:e.target.value})} className="filter-input" style={{ width:'100%',marginTop:'6px' }} required />
                </div>
                <div style={{ gridColumn:'span 2' }}>
                  <label style={{ fontSize:'0.75rem',fontWeight:'800',color:'#64748b',textTransform:'uppercase',letterSpacing:'0.5px' }}>E-mail de Acesso</label>
                  <input type="email" placeholder="email@exemplo.com" value={studentForm.email} onChange={e => setStudentForm({...studentForm,email:e.target.value})} className="filter-input" style={{ width:'100%',marginTop:'6px' }} required />
                  <p style={{ fontSize:'0.72rem',color:'#94a3b8',margin:'5px 0 0' }}>Use @quevedo.temp se o e-mail real não foi confirmado ainda.</p>
                </div>
                <div>
                  <label style={{ fontSize:'0.75rem',fontWeight:'800',color:'#64748b',textTransform:'uppercase',letterSpacing:'0.5px' }}>Plano</label>
                  <select value={studentForm.plan} onChange={e => setStudentForm({...studentForm,plan:e.target.value})} className="filter-input" style={{ width:'100%',marginTop:'6px' }}>
                    <option value="kickstart">Kickstart ({PLAN_MONTHS.kickstart} meses)</option>
                    <option value="elite_squad">Elite Squad ({PLAN_MONTHS.elite_squad} meses)</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize:'0.75rem',fontWeight:'800',color:'#64748b',textTransform:'uppercase',letterSpacing:'0.5px' }}>Nível</label>
                  <select value={studentForm.level} onChange={e => setStudentForm({...studentForm,level:e.target.value})} className="filter-input" style={{ width:'100%',marginTop:'6px' }}>
                    {LEVELS.map(lv => <option key={lv} value={lv}>{lv}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize:'0.75rem',fontWeight:'800',color:'#64748b',textTransform:'uppercase',letterSpacing:'0.5px' }}>Início do Contrato</label>
                  <input type="date" value={studentForm.contract_start} onChange={e => setStudentForm({...studentForm,contract_start:e.target.value})} className="filter-input" style={{ width:'100%',marginTop:'6px' }} />
                </div>
                <div>
                  <label style={{ fontSize:'0.75rem',fontWeight:'800',color:'#64748b',textTransform:'uppercase',letterSpacing:'0.5px' }}>Término (auto)</label>
                  <input type="text" readOnly value={studentForm.contract_start ? calcContractEnd(studentForm.plan,studentForm.contract_start) : '—'} className="filter-input" style={{ width:'100%',marginTop:'6px',background:'#f8fafc',color:'#94a3b8' }} />
                </div>
                <div style={{ gridColumn:'span 2' }}>
                  <label style={{ fontSize:'0.75rem',fontWeight:'800',color:'#64748b',textTransform:'uppercase',letterSpacing:'0.5px' }}>Telefone (WhatsApp)</label>
                  <input type="tel" placeholder="5531999999999" value={studentForm.phone} onChange={e => setStudentForm({...studentForm,phone:e.target.value})} className="filter-input" style={{ width:'100%',marginTop:'6px' }} />
                </div>
                <div style={{ gridColumn:'span 2' }}>
                  <label style={{ display:'flex',alignItems:'center',gap:'10px',cursor:'pointer',fontWeight:'700',color:'#1a2a6c',fontSize:'0.88rem',background:'#f8fafc',padding:'12px',borderRadius:'10px' }}>
                    <input type="checkbox" checked={studentForm.is_trial} onChange={e => setStudentForm({...studentForm,is_trial:e.target.checked})} style={{ transform:'scale(1.2)' }} />
                    Marcar como aluno Trial
                  </label>
                </div>
                <div style={{ gridColumn:'span 2' }}>
                  <label style={{ fontSize:'0.75rem',fontWeight:'800',color:'#64748b',textTransform:'uppercase',letterSpacing:'0.5px',display:'block',marginBottom:'8px' }}>Portal do Aluno</label>
                  <div style={{ display:'flex',gap:'8px' }}>
                    <button type="button" onClick={() => setStudentForm({...studentForm,language:'english'})} style={{ flex:1,padding:'10px',borderRadius:'8px',border:'2px solid',borderColor:studentForm.language==='english'?'#1a2a6c':'#e2e8f0',background:studentForm.language==='english'?'#dbeafe':'white',color:studentForm.language==='english'?'#1d4ed8':'#94a3b8',fontWeight:'800',cursor:'pointer',fontFamily:'Raleway',fontSize:'0.82rem' }}>🇧🇷 English</button>
                    <button type="button" onClick={() => setStudentForm({...studentForm,language:'spanish'})} style={{ flex:1,padding:'10px',borderRadius:'8px',border:'2px solid',borderColor:studentForm.language==='spanish'?'#dc2626':'#e2e8f0',background:studentForm.language==='spanish'?'#fee2e2':'white',color:studentForm.language==='spanish'?'#dc2626':'#94a3b8',fontWeight:'800',cursor:'pointer',fontFamily:'Raleway',fontSize:'0.82rem' }}>🇪🇸 Español</button>
                  </div>
                </div>
                <div style={{ gridColumn:'span 2' }}>
                  <label style={{ fontSize:'0.75rem',fontWeight:'800',color:'#64748b',textTransform:'uppercase',letterSpacing:'0.5px',display:'block',marginBottom:'8px' }}>Idioma da Interface</label>
                  <div style={{ display:'flex',gap:'8px' }}>
                    {[['pt','🇧🇷 Português'],['es','🇪🇸 Español'],['en','🇬🇧 English']].map(([val,label]) => (
                      <button type="button" key={val} onClick={() => setStudentForm({...studentForm,ui_language:val})} style={{ flex:1,padding:'10px',borderRadius:'8px',border:'2px solid',borderColor:studentForm.ui_language===val?'#1a2a6c':'#e2e8f0',background:studentForm.ui_language===val?'#dbeafe':'white',color:studentForm.ui_language===val?'#1d4ed8':'#94a3b8',fontWeight:'800',cursor:'pointer',fontFamily:'Raleway',fontSize:'0.75rem' }}>{label}</button>
                    ))}
                  </div>
                </div>
              </div>
              <div style={{ display:'flex',gap:'10px' }}>
                <button type="button" onClick={() => setStudentModalOpen(false)} style={{ flex:1,padding:'14px',background:'white',color:'#64748b',border:'1.5px solid #e2e8f0',borderRadius:'12px',fontWeight:'700',cursor:'pointer',fontFamily:'Raleway' }}>Cancelar</button>
                <button type="submit" disabled={loading} style={{ flex:2,padding:'14px',background:'linear-gradient(135deg,#16a34a,#15803d)',color:'white',border:'none',borderRadius:'12px',fontWeight:'800',cursor:'pointer',fontFamily:'Raleway' }}>{loading?'Criando...':'✓ Criar Aluno'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* TRIAL INFO POPUP */}
      {trialPopupOpen && trialStudent && (
        <div className="modal-overlay" onClick={e => e.target.className==='modal-overlay' && setTrialPopupOpen(false)}>
          <div className="modal-box" style={{ maxWidth:'480px',padding:'36px' }}>
            <button className="modal-close" onClick={() => setTrialPopupOpen(false)}>✕</button>
            <div style={{ display:'flex',alignItems:'center',gap:'12px',marginBottom:'20px',paddingRight:'40px' }}>
              <div style={{ width:'48px',height:'48px',borderRadius:'50%',background:'linear-gradient(135deg,#7c3aed,#a855f7)',color:'white',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:'900',fontSize:'1.2rem',flexShrink:0 }}>
                {trialStudent.full_name.charAt(0).toUpperCase()}
              </div>
              <div>
                <h3 style={{ margin:'0 0 4px',fontWeight:'900',color:'#0f172a' }}>{trialStudent.full_name}</h3>
                <span className="badge" style={{ background:'#ede9fe',color:'#7c3aed' }}>🆕 Aluno Trial</span>
              </div>
            </div>
            <div style={{ background:'#faf5ff',borderRadius:'12px',padding:'16px',marginBottom:'20px',border:'1.5px solid #e9d5ff' }}>
              <div style={{ fontSize:'0.72rem',fontWeight:'800',color:'#7c3aed',textTransform:'uppercase',letterSpacing:'1px',marginBottom:'10px' }}>O que é Trial?</div>
              <p style={{ margin:0,fontSize:'0.85rem',color:'#475569',lineHeight:1.6,fontWeight:'600' }}>
                Aluno em período de teste — ainda não fez compromisso formal com o plano. Acompanhe de perto a participação e experiência. Quando pronto, converta para aluno regular.
              </p>
            </div>
            <div style={{ marginBottom:'20px' }}>
              <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px',marginBottom:'16px' }}>
                {[
                  { label: 'Aulas Assistidas', value: trialStudent.totalAttended, color: '#1a2a6c' },
                  { label: 'Presença', value: trialStudent.attRate!==null ? `${trialStudent.attRate}%` : '—', color: '#16a34a' },
                  { label: 'Speaking Club', value: trialStudent.scAtt||0, color: '#0d9488' },
                ].map(({label,value,color}) => (
                  <div key={label} style={{ background:'#f8fafc',borderRadius:'10px',padding:'12px',textAlign:'center' }}>
                    <div style={{ fontSize:'1.4rem',fontWeight:'900',color }}>{value}</div>
                    <div style={{ fontSize:'0.65rem',fontWeight:'700',color:'#94a3b8',marginTop:'3px',textTransform:'uppercase',letterSpacing:'0.5px',lineHeight:1.3 }}>{label}</div>
                  </div>
                ))}
              </div>
              <label style={{ fontSize:'0.75rem',fontWeight:'800',color:'#64748b',textTransform:'uppercase',letterSpacing:'0.5px',display:'block',marginBottom:'6px' }}>Anotações sobre este Trial</label>
              <textarea rows={4} value={trialNote} onChange={e => setTrialNote(e.target.value)}
                placeholder="Como está a evolução? Algum ponto de atenção? Está pronto para converter?"
                style={{ width:'100%',padding:'12px',borderRadius:'10px',border:'1.5px solid #e2e8f0',outline:'none',boxSizing:'border-box',fontSize:'0.88rem',lineHeight:1.6,fontFamily:'Raleway' }} />
              <button onClick={saveTrialNote} disabled={trialSaving} style={{ marginTop:'8px',padding:'9px 18px',background:'#7c3aed',color:'white',border:'none',borderRadius:'8px',fontWeight:'800',cursor:'pointer',fontFamily:'Raleway',fontSize:'0.82rem' }}>
                {trialSaving ? 'Salvando...' : '💾 Salvar Anotação'}
              </button>
            </div>
            <div style={{ display:'flex',gap:'10px' }}>
              <button onClick={() => setTrialPopupOpen(false)} style={{ flex:1,padding:'12px',background:'white',color:'#64748b',border:'1.5px solid #e2e8f0',borderRadius:'12px',fontWeight:'700',cursor:'pointer',fontFamily:'Raleway' }}>Fechar</button>
              <button onClick={() => { toggleFlag(trialStudent,'is_trial'); setTrialPopupOpen(false); }} style={{ flex:1,padding:'12px',background:'linear-gradient(135deg,#16a34a,#15803d)',color:'white',border:'none',borderRadius:'12px',fontWeight:'800',cursor:'pointer',fontFamily:'Raleway' }}>✓ Converter p/ Regular</button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT CONTACT MODAL */}
      {contactModalOpen && selStudent && (
        <div className="modal-overlay" onClick={e => e.target.className==='modal-overlay' && setContactModalOpen(false)}>
          <div className="modal-box" style={{ maxWidth:'420px',padding:'36px' }}>
            <button className="modal-close" onClick={() => setContactModalOpen(false)}>✕</button>
            <h2 style={{ margin:'0 0 6px',fontWeight:'800',color:'#0f172a',paddingRight:'40px' }}>✏️ Editar Contato</h2>
            <p style={{ color:'#94a3b8',fontSize:'0.82rem',marginBottom:'24px' }}>{selStudent.full_name}</p>
            <div style={{ marginBottom:'14px' }}>
              <label style={{ fontSize:'0.75rem',fontWeight:'800',color:'#64748b',textTransform:'uppercase',letterSpacing:'0.5px' }}>E-mail de Acesso</label>
              <input type="email" value={contactForm.email} onChange={e => setContactForm({...contactForm,email:e.target.value})} className="filter-input" style={{ width:'100%',marginTop:'6px' }} />
            </div>
            <div style={{ marginBottom:'24px' }}>
              <label style={{ fontSize:'0.75rem',fontWeight:'800',color:'#64748b',textTransform:'uppercase',letterSpacing:'0.5px' }}>Telefone (WhatsApp)</label>
              <input type="tel" placeholder="5531999999999" value={contactForm.phone} onChange={e => setContactForm({...contactForm,phone:e.target.value})} className="filter-input" style={{ width:'100%',marginTop:'6px' }} />
            </div>
            <div style={{ display:'flex',gap:'10px' }}>
              <button onClick={() => setContactModalOpen(false)} style={{ flex:1,padding:'14px',background:'white',color:'#64748b',border:'1.5px solid #e2e8f0',borderRadius:'12px',fontWeight:'700',cursor:'pointer',fontFamily:'Raleway' }}>Cancelar</button>
              <button onClick={saveContact} disabled={contactSaving} style={{ flex:1,padding:'14px',background:'linear-gradient(135deg,#0284c7,#0369a1)',color:'white',border:'none',borderRadius:'12px',fontWeight:'800',cursor:'pointer',fontFamily:'Raleway' }}>{contactSaving?'Salvando...':'✓ Salvar'}</button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE MODAL */}
      {deleteModalOpen && (
        <div className="modal-overlay" onClick={e => e.target.className==='modal-overlay' && setDeleteModalOpen(false)}>
          <div className="modal-box" style={{ maxWidth:'420px',padding:'36px' }}>
            <button className="modal-close" onClick={() => setDeleteModalOpen(false)}>✕</button>
            <div style={{ textAlign:'center',marginBottom:'24px' }}>
              <div style={{ fontSize:'3rem',marginBottom:'12px' }}>🗑️</div>
              <h2 style={{ margin:0,fontWeight:'800',color:'#0f172a' }}>Excluir Aula</h2>
              <p style={{ color:'#64748b',fontSize:'0.9rem',marginTop:'8px' }}>Alunos inscritos perderão suas vagas.</p>
            </div>
            <div style={{ display:'flex',flexDirection:'column',gap:'10px',marginBottom:'28px' }}>
              {[['single','Apenas esta aula'],['future','Esta e as próximas da série'],['all','Todas as aulas da série']].map(([val,label]) => (
                <label key={val} style={{ display:'flex',alignItems:'center',gap:'12px',cursor:'pointer',background:deleteScope===val?'#fee2e2':'#f8fafc',padding:'14px',borderRadius:'10px',fontWeight:'700',color:deleteScope===val?'#dc2626':'#475569',border:`1.5px solid ${deleteScope===val?'#fecaca':'#f1f5f9'}`,transition:'all 0.15s' }}>
                  <input type="radio" name="del" value={val} checked={deleteScope===val} onChange={() => setDeleteScope(val)} />
                  {label}
                </label>
              ))}
            </div>
            <div style={{ display:'flex',gap:'10px' }}>
              <button onClick={() => setDeleteModalOpen(false)} style={{ flex:1,padding:'14px',background:'white',color:'#64748b',border:'1.5px solid #e2e8f0',borderRadius:'12px',fontWeight:'700',cursor:'pointer',fontFamily:'Raleway' }}>Cancelar</button>
              <button onClick={confirmDelete} disabled={loading} style={{ flex:1,padding:'14px',background:'#dc2626',color:'white',border:'none',borderRadius:'12px',fontWeight:'800',cursor:'pointer',fontFamily:'Raleway' }}>{loading?'Excluindo...':'Excluir'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ATTENDANCE MODAL */}
      {attModalOpen && attLesson && (
        <div className="modal-overlay" onClick={e => e.target.className==='modal-overlay' && setAttModalOpen(false)}>
          <div className="modal-box" style={{ maxWidth:'600px',padding:'36px' }}>
            <button className="modal-close" onClick={() => setAttModalOpen(false)}>✕</button>
            <h2 style={{ margin:'0 0 4px',fontWeight:'800',color:'#0f172a',paddingRight:'40px' }}>📋 Registro de Presença</h2>
            <p style={{ color:'#64748b',fontSize:'0.88rem',marginBottom:'24px' }}>{attLesson.title} · {new Date(attLesson.class_time).toLocaleString('pt-BR',{weekday:'long',day:'2-digit',month:'long',hour:'2-digit',minute:'2-digit',timeZone:'America/Sao_Paulo'})} · Prof. {attLesson.teacher_name}</p>
            <div className="profile-section">
              <div className="profile-section-title">Alunos Inscritos ({attLesson.enrolled.length})</div>
              {attLesson.enrolled.length===0
                ? <p style={{ color:'#94a3b8',fontStyle:'italic' }}>Nenhum aluno inscrito.</p>
                : <div style={{ display:'flex',flexDirection:'column',gap:'10px' }}>
                    {attLesson.enrolled.map(sig => {
                      const entry = attData[sig.student_email]||{status:null,note:''};
                      return (
                        <div key={sig.student_email} style={{ background:'#f8fafc',borderRadius:'12px',padding:'14px',border:'1.5px solid',borderColor:entry.status?ATT[entry.status]?.bg:'#f1f5f9' }}>
                          <div style={{ display:'flex',alignItems:'center',gap:'12px',marginBottom:'10px' }}>
                            <div style={{ width:'36px',height:'36px',borderRadius:'50%',background:'linear-gradient(135deg,#1a2a6c,#2563eb)',color:'white',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:'800',fontSize:'0.9rem',flexShrink:0 }}>
                              {getStudentName(sig.student_email).charAt(0).toUpperCase()}
                            </div>
                            <div style={{ flex:1 }}>
                              <div style={{ fontWeight:'700',color:'#0f172a',fontSize:'0.9rem' }}>{getStudentName(sig.student_email)}</div>
                              <div style={{ fontSize:'0.72rem',color:'#94a3b8' }}>{sig.student_email}</div>
                            </div>
                            <div style={{ display:'flex',gap:'6px' }}>
                              {Object.entries(ATT).map(([key,val]) => (
                                <button key={key} className="att-btn"
                                  style={{ background:entry.status===key?val.bg:'white',color:entry.status===key?val.color:'#94a3b8',borderColor:entry.status===key?val.color:'#e2e8f0',minWidth:'72px' }}
                                  onClick={() => setAttData(prev => ({...prev,[sig.student_email]:{...prev[sig.student_email],status:key}}))}>
                                  {val.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          <input type="text" placeholder="Observação sobre esta aula (opcional)..." value={entry.note}
                            onChange={e => setAttData(prev => ({...prev,[sig.student_email]:{...prev[sig.student_email],note:e.target.value}}))}
                            style={{ width:'100%',padding:'8px 12px',borderRadius:'8px',border:'1.5px solid #e2e8f0',outline:'none',fontSize:'0.82rem',fontFamily:'Raleway',boxSizing:'border-box' }} />
                        </div>
                      );
                    })}
                  </div>
              }
            </div>
            <div className="profile-section">
              <div className="profile-section-title">📚 Conteúdo da Aula</div>
              <div style={{ display:'flex',flexDirection:'column',gap:'10px' }}>
                {[['content_covered','Conteúdo abordado'],['homework','Tarefa de casa'],['teacher_notes','Observações do professor']].map(([field,label]) => (
                  <div key={field}>
                    <label style={{ fontSize:'0.75rem',fontWeight:'800',color:'#64748b',display:'block',marginBottom:'5px' }}>{label}</label>
                    <textarea rows={2} value={matData[field]} onChange={e => setMatData(prev => ({...prev,[field]:e.target.value}))} placeholder={`${label}...`}
                      style={{ width:'100%',padding:'10px 12px',borderRadius:'8px',border:'1.5px solid #e2e8f0',outline:'none',boxSizing:'border-box',fontFamily:'Raleway',fontSize:'0.85rem' }} />
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display:'flex',gap:'10px' }}>
              <button onClick={() => setAttModalOpen(false)} style={{ flex:1,padding:'14px',background:'white',color:'#64748b',border:'1.5px solid #e2e8f0',borderRadius:'12px',fontWeight:'700',cursor:'pointer',fontFamily:'Raleway' }}>Cancelar</button>
              <button onClick={saveAttendance} disabled={loading} style={{ flex:2,padding:'14px',background:'linear-gradient(135deg,#16a34a,#15803d)',color:'white',border:'none',borderRadius:'12px',fontWeight:'800',cursor:'pointer',fontFamily:'Raleway' }}>{loading?'Salvando...':'✅ Salvar Presença'}</button>
            </div>
          </div>
        </div>
      )}

      {/* STUDENT PROFILE MODAL */}
      {profileOpen && selStudent && (
        <div className="modal-overlay" onClick={e => e.target.className==='modal-overlay' && setProfileOpen(false)}>
          <div className="modal-box" style={{ maxWidth:'620px',padding:'36px' }}>
            <button className="modal-close" onClick={() => setProfileOpen(false)}>✕</button>
            <div style={{ display:'flex',alignItems:'center',gap:'16px',marginBottom:'20px',paddingBottom:'20px',borderBottom:'1px solid #f1f5f9',paddingRight:'40px' }}>
              <div style={{ width:'56px',height:'56px',borderRadius:'50%',background:'linear-gradient(135deg,#1a2a6c,#2563eb)',color:'white',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:'900',fontSize:'1.4rem',flexShrink:0 }}>
                {selStudent.full_name.charAt(0).toUpperCase()}
              </div>
              <div style={{ flex:1 }}>
                <h2 style={{ margin:'0 0 4px',fontWeight:'900',color:'#0f172a' }}>{selStudent.full_name}</h2>
                <div style={{ fontSize:'0.82rem',color:'#94a3b8',marginBottom:'6px' }}>{selStudent.email}</div>
                {selStudent.phone && <div style={{ fontSize:'0.8rem',color:'#64748b',marginBottom:'8px' }}>📞 {selStudent.phone}</div>}
                <div style={{ display:'flex',gap:'8px',flexWrap:'wrap' }}>
                  <span className="badge" style={{ background:(PLAN_COLORS[selStudent.plan]||{}).bg,color:(PLAN_COLORS[selStudent.plan]||{}).color }}>{PLAN_LABELS[selStudent.plan]}</span>
                  <span className="badge" style={{ background:(levelColor[selStudent.level]||{}).bg,color:(levelColor[selStudent.level]||{}).color }}>{selStudent.level}</span>
                  {selStudent.is_trial && <span className="badge" style={{ background:'#ede9fe',color:'#7c3aed' }}>🆕 Trial</span>}
                  {selStudent.isAtRisk && <span className="badge" style={{ background:'#fee2e2',color:'#dc2626' }}>⚠️ Em Risco</span>}
                  {selStudent.ready_for_levelup && <span className="badge" style={{ background:'#fef3c7',color:'#d97706' }}>📈 Pronto p/ Subir</span>}
                  {selStudent.elite_completed && <span className="badge" style={{ background:'#fef3c7',color:'#d97706' }}>👑 Elite</span>}
                </div>
              </div>
              <div style={{ display:'flex',gap:'8px',flexDirection:'column',alignItems:'flex-end' }}>
                <button onClick={() => openContactModal(selStudent)} className="btn-action" style={{ background:'#f0f9ff',color:'#0284c7',borderColor:'#bae6fd',fontSize:'0.72rem' }}>✏️ Editar Contato</button>
                <button onClick={() => toggleFlag(selStudent,'is_trial')} className="btn-action" style={{ background:selStudent.is_trial?'#ede9fe':'white',color:'#7c3aed',borderColor:'#ddd6fe',fontSize:'0.72rem' }}>{selStudent.is_trial?'✓ Trial':'+ Trial'}</button>
                <button onClick={() => toggleFlag(selStudent,'ready_for_levelup')} className="btn-action" style={{ background:selStudent.ready_for_levelup?'#fef3c7':'white',color:'#d97706',borderColor:'#fde68a',fontSize:'0.72rem' }}>{selStudent.ready_for_levelup?'✓ Pronto':'+ Subir Nível'}</button>
                <button onClick={() => toggleFlag(selStudent,'elite_completed')} className="btn-action" style={{ background:selStudent.elite_completed?'#fef3c7':'white',color:'#d97706',borderColor:'#fde68a',fontSize:'0.72rem' }}>{selStudent.elite_completed?'👑 Elite ✓':'+ Elite Completo'}</button>
                {selStudent.contract_status === 'cancelled' ? (
                  <button onClick={() => reactivateContract(selStudent)} className="btn-action" style={{ background:'#f0fdf4',color:'#16a34a',borderColor:'#bbf7d0',fontSize:'0.72rem' }}>↺ Reativar Contrato</button>
                ) : (
                  <button onClick={() => cancelContract(selStudent)} className="btn-action" style={{ background:'white',color:'#b45309',borderColor:'#fde68a',fontSize:'0.72rem' }}>⏸️ Cancelar Contrato</button>
                )}
                <button onClick={() => deleteStudent(selStudent)} className="btn-action" style={{ background:'white',color:'#dc2626',borderColor:'#fecaca',fontSize:'0.72rem' }}>🗑️ Excluir Aluno</button>
              </div>
            </div>

            <div style={{ display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'12px',marginBottom:'24px' }}>
              {[
                { label:'Aulas Assistidas',value:selStudent.totalAttended,color:'#16a34a' },
                { label:'Presença',value:selStudent.attRate!==null?`${selStudent.attRate}%`:'—',color:selStudent.attRate>=80?'#16a34a':selStudent.attRate>=60?'#d97706':'#dc2626' },
                { label:'Speaking Club',value:selStudent.scAtt||0,color:'#0d9488' },
              ].map(({label,value,color}) => (
                <div key={label} style={{ background:'#f8fafc',borderRadius:'10px',padding:'14px',textAlign:'center' }}>
                  <div style={{ fontSize:'1.4rem',fontWeight:'900',color }}>{value}</div>
                  <div style={{ fontSize:'0.7rem',fontWeight:'700',color:'#94a3b8',marginTop:'4px',textTransform:'uppercase',letterSpacing:'0.5px' }}>{label}</div>
                </div>
              ))}
            </div>

            <div className="profile-section">
              <div className="profile-section-title">Plano</div>
              {!SELLABLE_PLANS.includes(selStudent.plan) && selStudent.plan && (
                <p style={{ fontSize:'0.72rem', color:'#94a3b8', margin:'0 0 8px' }}>
                  Atualmente em um plano legado ({PLAN_LABELS[selStudent.plan] || selStudent.plan}). Escolher uma opção abaixo migra o aluno para o novo plano.
                </p>
              )}
              <div style={{ display:'flex',gap:'8px',marginBottom:'16px' }}>
                {SELLABLE_PLANS.map(key => (
                  <button key={key} onClick={() => handlePlanChange(selStudent,key)} style={{ flex:1,padding:'10px 6px',borderRadius:'8px',border:'2px solid',borderColor:selStudent.plan===key?(PLAN_COLORS[key]||{}).color:'#e2e8f0',background:selStudent.plan===key?(PLAN_COLORS[key]||{}).bg:'white',color:selStudent.plan===key?(PLAN_COLORS[key]||{}).color:'#94a3b8',fontWeight:'800',cursor:'pointer',fontFamily:'Raleway',fontSize:'0.75rem',transition:'all 0.15s' }}>
                    {PLAN_LABELS[key]}
                  </button>
                ))}
              </div>
              <div className="profile-section-title">Nível</div>
              <div style={{ display:'flex',gap:'8px',flexWrap:'wrap' }}>
                {LEVELS.map(lv => (
                  <button key={lv} onClick={() => handleLevelChange(selStudent,lv)} style={{ flex:1,minWidth:'100px',padding:'10px',borderRadius:'8px',border:'2px solid',borderColor:selStudent.level===lv?(levelColor[lv]||{}).color:'#e2e8f0',background:selStudent.level===lv?(levelColor[lv]||{}).bg:'white',color:selStudent.level===lv?(levelColor[lv]||{}).color:'#94a3b8',fontWeight:'800',cursor:'pointer',fontFamily:'Raleway',fontSize:'0.78rem',transition:'all 0.15s' }}>
                    {lv}
                  </button>
                ))}
              </div>
            </div>

            <div className="profile-section">
              <div className="profile-section-title">Contrato</div>
              <div style={{ background:'#f8fafc',borderRadius:'10px',padding:'14px',display:'flex',gap:'20px',flexWrap:'wrap' }}>
                <div><div style={{ fontSize:'0.72rem',fontWeight:'700',color:'#94a3b8',textTransform:'uppercase',marginBottom:'4px' }}>Início</div><div style={{ fontWeight:'700',color:'#0f172a' }}>{selStudent.contract_start?new Date(selStudent.contract_start).toLocaleDateString('pt-BR',{timeZone:'UTC'}):'—'}</div></div>
                <div><div style={{ fontSize:'0.72rem',fontWeight:'700',color:'#94a3b8',textTransform:'uppercase',marginBottom:'4px' }}>Término</div><div style={{ fontWeight:'700',color:'#0f172a' }}>{selStudent.contract_end?new Date(selStudent.contract_end).toLocaleDateString('pt-BR',{timeZone:'UTC'}):'—'}</div></div>
                {selStudent.contract_end && <div><div style={{ fontSize:'0.72rem',fontWeight:'700',color:'#94a3b8',textTransform:'uppercase',marginBottom:'4px' }}>Status</div><span className="badge" style={{ background:expiryStyle(daysLeft(selStudent.contract_end)).bg,color:expiryStyle(daysLeft(selStudent.contract_end)).color }}>{daysLeft(selStudent.contract_end)<0?'Expirado':`${daysLeft(selStudent.contract_end)} dias restantes`}</span></div>}
              </div>
            </div>

            {selStudent.levelHistory?.length>0 && (
              <div className="profile-section">
                <div className="profile-section-title">Histórico de Nível</div>
                <div style={{ display:'flex',flexDirection:'column',gap:'6px' }}>
                  {selStudent.levelHistory.slice(0,5).map(h => (
                    <div key={h.id} style={{ display:'flex',alignItems:'center',gap:'10px',padding:'8px 12px',background:'#f8fafc',borderRadius:'8px',fontSize:'0.82rem' }}>
                      <span className="badge" style={{ background:(levelColor[h.old_level]||{}).bg,color:(levelColor[h.old_level]||{}).color }}>{h.old_level}</span>
                      <span style={{ color:'#94a3b8' }}>→</span>
                      <span className="badge" style={{ background:(levelColor[h.new_level]||{}).bg,color:(levelColor[h.new_level]||{}).color }}>{h.new_level}</span>
                      <span style={{ marginLeft:'auto',color:'#94a3b8' }}>{new Date(h.changed_at).toLocaleDateString('pt-BR',{timeZone:'America/Sao_Paulo'})}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="profile-section">
              <div className="profile-section-title">Anotações do Professor</div>
              <textarea rows={4} value={profileNote} onChange={e => setProfileNote(e.target.value)}
                placeholder="Observações gerais sobre o aluno, pontos fortes, áreas de melhoria..."
                style={{ width:'100%',padding:'12px',borderRadius:'10px',border:'1.5px solid #e2e8f0',outline:'none',boxSizing:'border-box',fontSize:'0.88rem',lineHeight:1.6,fontFamily:'Raleway' }} />
              <button onClick={saveProfileNote} disabled={profileSaving} style={{ marginTop:'10px',padding:'10px 20px',background:'#1a2a6c',color:'white',border:'none',borderRadius:'8px',fontWeight:'800',cursor:'pointer',fontFamily:'Raleway',fontSize:'0.82rem' }}>
                {profileSaving?'Salvando...':'💾 Salvar Anotação'}
              </button>
            </div>

            {selStudent.attRecords?.length>0 && (
              <div className="profile-section">
                <div className="profile-section-title">Histórico de Presença</div>
                <div style={{ display:'flex',flexDirection:'column',gap:'6px',maxHeight:'220px',overflowY:'auto' }}>
                  {selStudent.attRecords.slice(0,10).map(rec => {
                    const lesson = lessons.find(l => l.id===rec.lesson_id);
                    return (
                      <div key={rec.id} style={{ display:'flex',alignItems:'center',gap:'10px',padding:'10px 12px',background:'#f8fafc',borderRadius:'8px',fontSize:'0.82rem' }}>
                        <span className="badge" style={{ background:ATT[rec.status]?.bg,color:ATT[rec.status]?.color,minWidth:'72px',justifyContent:'center' }}>{ATT[rec.status]?.label}</span>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:'700',color:'#0f172a' }}>{lesson?.title||'—'}</div>
                          {rec.note && <div style={{ color:'#64748b',fontSize:'0.75rem',marginTop:'2px' }}>{rec.note}</div>}
                        </div>
                        <span style={{ color:'#94a3b8',whiteSpace:'nowrap' }}>{lesson?new Date(lesson.class_time).toLocaleDateString('pt-BR',{timeZone:'America/Sao_Paulo'}):'—'}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {selStudent.personal_goal && (
              <div className="profile-section">
                <div className="profile-section-title">🎯 Objetivo Pessoal</div>
                <div style={{ background:'linear-gradient(135deg,#f0f9ff,#e0f2fe)',border:'1.5px solid #bae6fd',borderRadius:'12px',padding:'14px 16px' }}>
                  <div style={{ fontWeight:'700',color:'#0f172a',lineHeight:1.5 }}>{selStudent.personal_goal}</div>
                </div>
              </div>
            )}

            {selStudent.reflections?.length > 0 && (
              <div className="profile-section">
                <div className="profile-section-title">✍️ Reflexões do Aluno</div>
                <div style={{ display:'flex',flexDirection:'column',gap:'10px',maxHeight:'260px',overflowY:'auto' }}>
                  {selStudent.reflections.slice(0,6).map((r,i) => (
                    <div key={i} style={{ background:'#fffbeb',border:'1.5px solid #fde047',borderRadius:'12px',padding:'14px' }}>
                      <div style={{ fontSize:'0.72rem',fontWeight:'800',color:'#92400e',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:'6px' }}>Semana {r.week_number}/{r.year}</div>
                      <div style={{ fontSize:'0.8rem',color:'#78350f',fontWeight:'700',marginBottom:'8px',fontStyle:'italic' }}>💬 {r.prompt}</div>
                      <div style={{ fontSize:'0.85rem',color:'#0f172a',lineHeight:1.5,fontWeight:'600' }}>{r.response || <em style={{ color:'#94a3b8' }}>Sem resposta ainda</em>}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selStudent.checkins?.length > 0 && (
              <div className="profile-section">
                <div className="profile-section-title">📊 Check-ins Semanais</div>
                <div style={{ display:'flex',flexDirection:'column',gap:'8px',maxHeight:'200px',overflowY:'auto' }}>
                  {selStudent.checkins.slice(0,8).map((c,i) => (
                    <div key={i} style={{ display:'flex',gap:'12px',alignItems:'flex-start',padding:'10px 12px',background:'#f8fafc',borderRadius:'10px' }}>
                      <div style={{ fontSize:'0.7rem',fontWeight:'700',color:'#94a3b8',whiteSpace:'nowrap',marginTop:'2px' }}>S{c.week_number}</div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:'0.75rem',color:'#64748b',fontWeight:'600',marginBottom:'3px' }}>{c.question}</div>
                        <div style={{ fontSize:'0.82rem',fontWeight:'800',color:'#0f172a' }}>{c.answer}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selStudent.cancellations?.length > 0 && (
              <div className="profile-section">
                <div className="profile-section-title">🗑️ Cancelamentos ({selStudent.cancellations.length})</div>
                <div style={{ display:'flex',flexDirection:'column',gap:'6px',maxHeight:'160px',overflowY:'auto' }}>
                  {selStudent.cancellations.slice(0,6).map((c,i) => (
                    <div key={i} style={{ display:'flex',gap:'12px',alignItems:'center',padding:'8px 12px',background:'#fff1f2',borderRadius:'8px',fontSize:'0.78rem' }}>
                      <span style={{ color:'#dc2626',fontWeight:'700',whiteSpace:'nowrap' }}>{new Date(c.cancelled_at).toLocaleDateString('pt-BR',{timeZone:'America/Sao_Paulo'})}</span>
                      <span style={{ color:'#64748b',fontWeight:'600' }}>{c.reason || 'Sem motivo informado'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button onClick={() => setProfileOpen(false)} style={{ width:'100%',padding:'14px',background:'white',color:'#64748b',border:'1.5px solid #e2e8f0',borderRadius:'12px',fontWeight:'700',cursor:'pointer',fontFamily:'Raleway',marginTop:'4px' }}>Fechar</button>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================
// COMMUNITY SECTION WRAPPER
// Sub-tabs: Recommendations (community_posts) / Media (community_media)
// ============================================================
function AdminCommunitySection({ onCountChange }) {
  const [subTab, setSubTab] = useState('posts');
  const [postsPending, setPostsPending] = useState(0);
  const [mediaPending, setMediaPending] = useState(0);

  useEffect(() => { onCountChange?.(postsPending + mediaPending); }, [postsPending, mediaPending, onCountChange]);

  return (
    <div>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
        <button className="btn-action" onClick={() => setSubTab('posts')} style={{ background: subTab === 'posts' ? '#1a2a6c' : 'white', color: subTab === 'posts' ? 'white' : '#475569', borderColor: subTab === 'posts' ? '#1a2a6c' : '#e2e8f0' }}>
          🌎 Recomendações {postsPending > 0 ? `(${postsPending})` : ''}
        </button>
        <button className="btn-action" onClick={() => setSubTab('media')} style={{ background: subTab === 'media' ? '#1a2a6c' : 'white', color: subTab === 'media' ? 'white' : '#475569', borderColor: subTab === 'media' ? '#1a2a6c' : '#e2e8f0' }}>
          🎬 Filmes, Podcasts e Livros {mediaPending > 0 ? `(${mediaPending})` : ''}
        </button>
      </div>
      {subTab === 'posts'
        ? <AdminCommunityTab onCountChange={setPostsPending} />
        : <AdminMediaTab onCountChange={setMediaPending} />}
    </div>
  );
}

// ============================================================
// COMMUNITY MODERATION TAB
// Approve/reject student recommendation posts (community_posts).
// Self-contained: fetches its own data, doesn't touch the main
// fetchAll() pipeline so it can't break the rest of the admin.
// ============================================================
function AdminCommunityTab({ onCountChange }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [busyId, setBusyId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('community_posts')
      .select('id, city, category, title, content, status, rejection_reason, created_at, students!community_posts_student_id_fkey(full_name, email)')
      .order('created_at', { ascending: false });
    setPosts(data || []);
    setLoading(false);
    const pendingCount = (data || []).filter(p => p.status === 'pending').length;
    onCountChange?.(pendingCount);
  }, [onCountChange]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    setBusyId(id);
    await supabase.from('community_posts').update({ status: 'approved', moderated_at: new Date().toISOString() }).eq('id', id);
    await load();
    setBusyId(null);
  };

  const openReject = (id) => { setRejectingId(id); setRejectReason(''); };

  const confirmReject = async () => {
    if (!rejectingId) return;
    setBusyId(rejectingId);
    await supabase.from('community_posts').update({ status: 'rejected', rejection_reason: rejectReason || null, moderated_at: new Date().toISOString() }).eq('id', rejectingId);
    setRejectingId(null);
    await load();
    setBusyId(null);
  };

  const revert = async (id) => {
    setBusyId(id);
    await supabase.from('community_posts').update({ status: 'pending', moderated_at: null, rejection_reason: null }).eq('id', id);
    await load();
    setBusyId(null);
  };

  const filtered = posts.filter(p => statusFilter === 'all' || p.status === statusFilter);

  const STATUS_BADGE = {
    pending:  { bg: '#fef3c7', color: '#d97706', label: '⏳ Pendente' },
    approved: { bg: '#dcfce7', color: '#166534', label: '✅ Aprovado' },
    rejected: { bg: '#fee2e2', color: '#dc2626', label: '🚫 Rejeitado' },
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap', background: 'white', padding: '16px', borderRadius: '14px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
        {['pending', 'approved', 'rejected', 'all'].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)} className="btn-action" style={{
            background: statusFilter === s ? '#1a2a6c' : 'white',
            color: statusFilter === s ? 'white' : '#475569',
            borderColor: statusFilter === s ? '#1a2a6c' : '#e2e8f0',
          }}>
            {s === 'pending' ? `⏳ Pendentes${posts.filter(p=>p.status==='pending').length ? ' (' + posts.filter(p=>p.status==='pending').length + ')' : ''}`
              : s === 'approved' ? '✅ Aprovados'
              : s === 'rejected' ? '🚫 Rejeitados'
              : '📋 Todos'}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Carregando...</div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
          {statusFilter === 'pending' ? '✅ Nenhuma recomendação pendente.' : 'Nenhuma recomendação encontrada.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filtered.map(p => (
            <div key={p.id} className="card" style={{ padding: '18px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span className="badge" style={{ background: STATUS_BADGE[p.status].bg, color: STATUS_BADGE[p.status].color }}>{STATUS_BADGE[p.status].label}</span>
                  <span className="badge" style={{ background: '#f0fdf4', color: '#16a34a' }}>{COMM_CATEGORY_ICON[p.category]} {COMM_CATEGORY_LABEL[p.category] || p.category}</span>
                  <span className="badge" style={{ background: '#f1f5f9', color: '#475569' }}>📍 {p.city}</span>
                </div>
                <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: '600', whiteSpace: 'nowrap' }}>
                  {new Date(p.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' })}
                </span>
              </div>

              <div style={{ fontWeight: '800', fontSize: '0.95rem', color: '#0f172a', marginBottom: '4px' }}>{p.title}</div>
              <div style={{ fontSize: '0.85rem', color: '#475569', lineHeight: 1.5, marginBottom: '8px' }}>{p.content}</div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: '700', marginBottom: '12px' }}>
                👤 {p.students?.full_name || 'Aluno desconhecido'} {p.students?.email ? `· ${p.students.email}` : ''}
              </div>

              {p.status === 'rejected' && p.rejection_reason && (
                <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '8px 12px', borderRadius: '8px', fontSize: '0.78rem', fontWeight: '600', marginBottom: '12px' }}>
                  Motivo: {p.rejection_reason}
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {p.status !== 'approved' && (
                  <button className="btn-action" disabled={busyId === p.id} onClick={() => approve(p.id)} style={{ background: '#dcfce7', color: '#166534', borderColor: '#bbf7d0' }}>
                    ✅ Aprovar
                  </button>
                )}
                {p.status !== 'rejected' && (
                  <button className="btn-action" disabled={busyId === p.id} onClick={() => openReject(p.id)} style={{ background: '#fee2e2', color: '#dc2626', borderColor: '#fecaca' }}>
                    🚫 Rejeitar
                  </button>
                )}
                {p.status !== 'pending' && (
                  <button className="btn-action" disabled={busyId === p.id} onClick={() => revert(p.id)} style={{ background: 'white', color: '#64748b', borderColor: '#e2e8f0' }}>
                    ↩️ Voltar para pendente
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {rejectingId && (
        <div className="modal-overlay" onClick={e => e.target.className === 'modal-overlay' && setRejectingId(null)}>
          <div className="modal-box" style={{ maxWidth: '420px', padding: '28px', textAlign: 'left' }}>
            <button className="modal-close" onClick={() => setRejectingId(null)}>✕</button>
            <h3 style={{ margin: '0 0 6px', fontWeight: '800', color: '#0f172a' }}>Rejeitar recomendação</h3>
            <p style={{ margin: '0 0 14px', fontSize: '0.82rem', color: '#94a3b8' }}>Motivo (opcional) — não é mostrado ao aluno automaticamente, é só para o time.</p>
            <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3} style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1.5px solid #e2e8f0', marginBottom: '14px' }} />
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setRejectingId(null)} style={{ flex: 1, padding: '12px', background: 'white', color: '#475569', border: '1.5px solid #e2e8f0', borderRadius: '10px', fontWeight: '700', cursor: 'pointer', fontFamily: 'Raleway' }}>Cancelar</button>
              <button onClick={confirmReject} style={{ flex: 1, padding: '12px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '800', cursor: 'pointer', fontFamily: 'Raleway' }}>Confirmar Rejeição</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// MEDIA MODERATION TAB
// Approve/reject student-submitted movies/podcasts/books (community_media).
// Curated (admin-seeded) rows are already 'approved' and just show up here read-only-ish.
// ============================================================
function AdminMediaTab({ onCountChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [busyId, setBusyId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('community_media')
      .select('id, media_type, title, level, description, link, status, curated_by, rejection_reason, created_at, students!community_media_student_id_fkey(full_name, email)')
      .order('created_at', { ascending: false });
    setItems(data || []);
    setLoading(false);
    const pendingCount = (data || []).filter(p => p.status === 'pending').length;
    onCountChange?.(pendingCount);
  }, [onCountChange]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    setBusyId(id);
    await supabase.from('community_media').update({ status: 'approved', moderated_at: new Date().toISOString() }).eq('id', id);
    await load();
    setBusyId(null);
  };

  const openReject = (id) => { setRejectingId(id); setRejectReason(''); };

  const confirmReject = async () => {
    if (!rejectingId) return;
    setBusyId(rejectingId);
    await supabase.from('community_media').update({ status: 'rejected', rejection_reason: rejectReason || null, moderated_at: new Date().toISOString() }).eq('id', rejectingId);
    setRejectingId(null);
    await load();
    setBusyId(null);
  };

  const revert = async (id) => {
    setBusyId(id);
    await supabase.from('community_media').update({ status: 'pending', moderated_at: null, rejection_reason: null }).eq('id', id);
    await load();
    setBusyId(null);
  };

  const filtered = items.filter(p => statusFilter === 'all' || p.status === statusFilter);

  const STATUS_BADGE = {
    pending:  { bg: '#fef3c7', color: '#d97706', label: '⏳ Pendente' },
    approved: { bg: '#dcfce7', color: '#166534', label: '✅ Aprovado' },
    rejected: { bg: '#fee2e2', color: '#dc2626', label: '🚫 Rejeitado' },
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap', background: 'white', padding: '16px', borderRadius: '14px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
        {['pending', 'approved', 'rejected', 'all'].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)} className="btn-action" style={{
            background: statusFilter === s ? '#1a2a6c' : 'white',
            color: statusFilter === s ? 'white' : '#475569',
            borderColor: statusFilter === s ? '#1a2a6c' : '#e2e8f0',
          }}>
            {s === 'pending' ? `⏳ Pendentes${items.filter(p=>p.status==='pending').length ? ' (' + items.filter(p=>p.status==='pending').length + ')' : ''}`
              : s === 'approved' ? '✅ Aprovados'
              : s === 'rejected' ? '🚫 Rejeitados'
              : '📋 Todos'}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Carregando...</div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
          {statusFilter === 'pending' ? '✅ Nenhum título pendente.' : 'Nenhum título encontrado.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filtered.map(p => (
            <div key={p.id} className="card" style={{ padding: '18px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span className="badge" style={{ background: STATUS_BADGE[p.status].bg, color: STATUS_BADGE[p.status].color }}>{STATUS_BADGE[p.status].label}</span>
                  <span className="badge" style={{ background: '#f0fdf4', color: '#16a34a' }}>{COMM_MEDIA_ICON[p.media_type]} {COMM_MEDIA_LABEL[p.media_type] || p.media_type}</span>
                  <span className="badge" style={{ background: '#f1f5f9', color: '#475569' }}>{p.level}</span>
                  {p.curated_by && <span className="badge" style={{ background: '#dbeafe', color: '#1d4ed8' }}>⭐ Curado</span>}
                </div>
                <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: '600', whiteSpace: 'nowrap' }}>
                  {new Date(p.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' })}
                </span>
              </div>

              <div style={{ fontWeight: '800', fontSize: '0.95rem', color: '#0f172a', marginBottom: '4px' }}>{p.title}</div>
              <div style={{ fontSize: '0.85rem', color: '#475569', lineHeight: 1.5, marginBottom: '8px' }}>{p.description}</div>
              {p.link && <div style={{ fontSize: '0.78rem', color: '#0284c7', fontWeight: '700', marginBottom: '8px', wordBreak: 'break-all' }}>🔗 {p.link}</div>}
              <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: '700', marginBottom: '12px' }}>
                👤 {p.curated_by || p.students?.full_name || 'Aluno desconhecido'} {p.students?.email ? `· ${p.students.email}` : ''}
              </div>

              {p.status === 'rejected' && p.rejection_reason && (
                <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '8px 12px', borderRadius: '8px', fontSize: '0.78rem', fontWeight: '600', marginBottom: '12px' }}>
                  Motivo: {p.rejection_reason}
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {p.status !== 'approved' && (
                  <button className="btn-action" disabled={busyId === p.id} onClick={() => approve(p.id)} style={{ background: '#dcfce7', color: '#166534', borderColor: '#bbf7d0' }}>
                    ✅ Aprovar
                  </button>
                )}
                {p.status !== 'rejected' && (
                  <button className="btn-action" disabled={busyId === p.id} onClick={() => openReject(p.id)} style={{ background: '#fee2e2', color: '#dc2626', borderColor: '#fecaca' }}>
                    🚫 Rejeitar
                  </button>
                )}
                {p.status !== 'pending' && (
                  <button className="btn-action" disabled={busyId === p.id} onClick={() => revert(p.id)} style={{ background: 'white', color: '#64748b', borderColor: '#e2e8f0' }}>
                    ↩️ Voltar para pendente
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {rejectingId && (
        <div className="modal-overlay" onClick={e => e.target.className === 'modal-overlay' && setRejectingId(null)}>
          <div className="modal-box" style={{ maxWidth: '420px', padding: '28px', textAlign: 'left' }}>
            <button className="modal-close" onClick={() => setRejectingId(null)}>✕</button>
            <h3 style={{ margin: '0 0 6px', fontWeight: '800', color: '#0f172a' }}>Rejeitar título</h3>
            <p style={{ margin: '0 0 14px', fontSize: '0.82rem', color: '#94a3b8' }}>Motivo (opcional) — não é mostrado ao aluno automaticamente, é só para o time.</p>
            <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3} style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1.5px solid #e2e8f0', marginBottom: '14px' }} />
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setRejectingId(null)} style={{ flex: 1, padding: '12px', background: 'white', color: '#475569', border: '1.5px solid #e2e8f0', borderRadius: '10px', fontWeight: '700', cursor: 'pointer', fontFamily: 'Raleway' }}>Cancelar</button>
              <button onClick={confirmReject} style={{ flex: 1, padding: '12px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '800', cursor: 'pointer', fontFamily: 'Raleway' }}>Confirmar Rejeição</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
